import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Grid, TextField, ToggleButtonGroup, ToggleButton, Paper,
  Chip, LinearProgress, Button, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Checkbox, FormControlLabel, Tooltip, MenuItem, IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Foundation as FoundationIcon,
  Build as BuildIcon,
  ElectricBolt as ElectricBoltIcon,
  Save as SaveIcon,
  LocalGasStation as GasIcon,
  Water as WaterIcon,
  Air as AirIcon,
  People as PeopleIcon,
  Compress as CompressIcon,
  LocalShipping as TankIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  ContentCopy as ContentCopyIcon,
  SwapHoriz as SwapHorizIcon,
  Science as ScienceIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  WarningAmber as WarningAmberIcon,
  InfoOutlined as InfoOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  MyLocation as MyLocationIcon,
  CloudOff as CloudOffIcon
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import inspectionEvaluationService from '../../services/inspectionEvaluationService';
import api from '../../config/axiosConfig';
import { buildHazardousSubstancesCatalogFallback } from '../../constants/hazardousSubstancesFallback';
import offlineEvaluationStore from '../../services/offlineEvaluationStore';
import offlineSyncEngine from '../../services/offlineSyncEngine';
import { OFFLINE_FIELD_MODE } from '../../config/features';
import asyncInspectionStore from '../../services/asyncInspectionStore';
import ReturnToValidationBanner, { goBackToValidation } from '../common/ReturnToValidation';

// Respaldo de la navegación (quotationId/branchId/...) en sessionStorage -- location.state de
// React Router no sobrevive una recarga de página (F5, o el SO restaurando la pestaña). Sin
// esto, recargar la pantalla estando sin señal deja la pantalla sin poder ni identificar qué
// ente estaba capturando (ver plan "Captura offline de Evaluación de Inspección", punto 5).
const EVAL_NAV_STORAGE_KEY = 'conem_eval_nav_state';

const readStoredNavState = () => {
  try {
    const raw = sessionStorage.getItem(EVAL_NAV_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const EvaluacionInspeccionCompleta = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Inspección "sin ente" (/evaluaciones/asincrona/:asyncId): se captura solo en el dispositivo
  // y se asigna a un ente después, desde Programas (ver asyncInspectionStore).
  const { asyncId } = useParams();
  const isAsync = !!asyncId;
  const storedNavState = isAsync ? null : readStoredNavState();
  const quotationId = isAsync ? null : (location.state?.quotationId ?? storedNavState?.quotationId); // Recibir quotation_id desde navegación
  const navCompanyId = location.state?.companyId ?? storedNavState?.companyId; // Opcional: companyId desde navegación
  const navCompanyName = location.state?.companyName ?? storedNavState?.companyName; // Opcional: nombre de empresa
  // Si llegamos desde "Validación de Campos" (Programas.jsx) con un ente y campo específico
  // a resaltar (Sustancias Peligrosas/Capacidad de Gas/Visto Bueno de PC), navBranchId +
  // navEntityType fijan el ente inicial en vez del default ("primer ente asignado").
  const navBranchId = location.state?.branchId ?? storedNavState?.branchId;
  const navEntityType = location.state?.entityType ?? storedNavState?.entityType;
  const navHighlightFieldKey = location.state?.highlightFieldKey ?? storedNavState?.highlightFieldKey;
  // Si llegó desde "Validar Campos" (Programas): botón para guardar y regresar a la validación
  const returnToValidation = location.state?.returnToValidation || null;

  useEffect(() => {
    if (!quotationId) return;
    try {
      sessionStorage.setItem(EVAL_NAV_STORAGE_KEY, JSON.stringify({
        quotationId, companyId: navCompanyId, companyName: navCompanyName,
        branchId: navBranchId, entityType: navEntityType, highlightFieldKey: navHighlightFieldKey
      }));
    } catch { /* storage lleno/bloqueado -- no es crítico, solo se pierde el respaldo de recarga */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId, navCompanyId, navCompanyName, navBranchId, navEntityType, navHighlightFieldKey]);

  // Estado offline (ver offlineSyncEngine.js): null = todo normal; si no null, se muestra el
  // banner correspondiente. 'cold-start' = se abrió esta evaluación sin señal y sin borrador
  // local previo (formulario en blanco). 'pending-sync' = hay cambios guardados localmente que
  // aún no llegan al servidor. 'auth-required' = la sesión venció mientras estaba offline.
  const [offlineBanner, setOfflineBanner] = useState(null);
  // Motivo con el que el servidor rechazó el borrador local de este ente ('sync-rejected')
  const [syncRejectedMessage, setSyncRejectedMessage] = useState('');
  // 'EVALUATION_NOT_FOUND' | 'CONFLICT' | null -- decide qué acciones ofrece el banner
  const [syncRejectedCode, setSyncRejectedCode] = useState(null);
  // Quién/cuándo guardó la versión del sistema y qué difiere (solo en conflicto)
  const [syncRejectedDetails, setSyncRejectedDetails] = useState(null);
  // De cuándo es el borrador del dispositivo y cuánto trae, para decidir en el banner
  const [draftInfo, setDraftInfo] = useState(null);
  const [discardDraftDialogOpen, setDiscardDraftDialogOpen] = useState(false);
  // Identificación de la inspección sin ente (nombre del sitio, notas, GPS)
  const [asyncMeta, setAsyncMeta] = useState({ siteName: '', notes: '', gps: null });
  const [locating, setLocating] = useState(false);

  // Estados para integración con backend
  const [evaluationId, setEvaluationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [evaluationInfo, setEvaluationInfo] = useState(null);

  // Datos "encontrados en sitio" por ente: sustancias peligrosas (de ahí sale también la
  // capacidad de gas del PIPC, ver DocumentService.resolveCapacidadGas) y visto bueno de
  // protección civil. Se heredan de la cotización al crear la evaluación (ver backend) y se
  // editan libremente aquí. quotedSiteData es de solo lectura, para mostrar "Cotizado: X"
  // junto a cada valor y compararlos.
  const [siteData, setSiteData] = useState({
    civilProtectionApproval: '',
    civilProtectionApprovalDate: '',
    civilProtectionApprovalValidity: '',
    noHazardousSubstances: false,
    hazardousSubstances: []
  });
  const [quotedSiteData, setQuotedSiteData] = useState({ hazardousSubstances: [] });
  const [hazardousSubstancesCatalog, setHazardousSubstancesCatalog] = useState([]);

  // Entity selection states
  const [entitiesStatus, setEntitiesStatus] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState({ entityType: 'MATRIZ', branchId: null, entityName: 'Matriz' });
  const [loadingEntities, setLoadingEntities] = useState(false);
  
  // Clone dialog states
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneTargets, setCloneTargets] = useState([]);

  // "Marcar todos como Bueno" -- solo rellena conceptos vacíos (nunca sobreescribe lo ya
  // capturado) y los deja marcados como "auto" (pendientes de confirmar) hasta que el
  // inspector los toque o los confirme explícitamente, para que no pasen como revisados
  // de verdad sin que nadie los haya visto.
  const [autoMarkedConcepts, setAutoMarkedConcepts] = useState(new Set());
  // Clave de la sección para la que se abrió el diálogo de confirmación (null = cerrado) --
  // un solo diálogo reutilizado por las 9 secciones, cada una con su propio botón.
  const [markAllDialogSection, setMarkAllDialogSection] = useState(null);
  // Bloqueo/recordatorio al intentar "Completar Evaluación" -- mismo patrón de Dialog (no
  // Snackbar) que "Marcar todos como Bueno", para que sea imposible de pasar por alto: null =
  // cerrado, { severity: 'warning'|'info', title, message } = abierto.
  const [completeIssueDialog, setCompleteIssueDialog] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [evaluaciones, setEvaluaciones] = useState({
    estructural: {
      conceptos: {
        ventanales: { estado: '', observaciones: '' },
        anunciosVolados: { estado: '', observaciones: '' },
        anunciosPanorámicos: { estado: '', observaciones: '' },
        marquesinas: { estado: '', observaciones: '' },
        acabadosFachada: { estado: '', observaciones: '' },
        pretilesBalcones: { estado: '', observaciones: '' },
        inclinacionInmueble: { estado: '', observaciones: '' },
        cimentacion: { estado: '', observaciones: '' },
        columnas: { estado: '', observaciones: '' },
        muros: { estado: '', observaciones: '' },
        bardasMallas: { estado: '', observaciones: '' },
        escaleras: { estado: '', observaciones: '' },
        arboles: { estado: '', observaciones: '' },
        rampasAuto: { estado: '', observaciones: '' },
        pasosDesnivel: { estado: '', observaciones: '' },
        estacionamiento: { estado: '', observaciones: '' }
      },
      camposTexto: {
        folioDictamen: '', vigenciaFecha: '', elaboradoPor: '',
        registroDRO: '', observacionesGenerales: ''
      }
    },
    equipoEspecifico: {
      conceptos: {
        bombas: { estado: '', observaciones: '' },
        moduloDespacho: { estado: '', observaciones: '' },
        parosEmergencia: { estado: '', observaciones: '' },
        mangueraDescarga: { estado: '', observaciones: '' },
        montacargas: { estado: '', observaciones: '' }
      },
      camposTexto: {
        folioNOM005ASEA: '', fechaEmisionASEA: '', unidadVerificacionASEA: '',
        registroUnidadASEA: '', observacionesASEA: '', folioNOM005SESH: '',
        casoMontacargas: '', fechaEmisionSESH: '', unidadVerificacionSESH: '',
        observacionesSESH: ''
      }
    },
    instalacionElectrica: {
      conceptos: {
        transformadorElectrico: { estado: '', observaciones: '' },
        subestacionElectrica: { estado: '', observaciones: '' },
        postesDistribucion: { estado: '', observaciones: '' },
        tablerosElectricos: { estado: '', observaciones: '' },
        equiposComputo: { estado: '', observaciones: '' },
        socketsFocos: { estado: '', observaciones: '' },
        tomaCorrientes: { estado: '', observaciones: '' },
        plantaEmergencia: { estado: '', observaciones: '' },
        aireAcondicionado: { estado: '', observaciones: '' }
      },
      camposTexto: {
        folioDictamenElectrico: '', fechaDictamen: '', unidadVerificacion: '',
        registroUnidad: '', observacionesGenerales: ''
      }
    },
    instalacionGas: {
      conceptos: {
        tuberiaGas: { estado: '', observaciones: '' },
        tanqueMovil: { estado: '', observaciones: '' },
        tanqueEstacionario: { estado: '', observaciones: '' },
        valvulasTanque: { estado: '', observaciones: '' }
      },
      camposTexto: {
        folioDictamenGas: '', unidadVerificacion: '', registroUnidad: '',
        fechaDictamen: '', fechaFabricacionTanques: '', folioNOM013: '',
        fechaDictamenNOM013: '', observacionesGenerales: ''
      }
    },
    instalacionHidrosanitaria: {
      conceptos: {
        cisterna: { estado: '', observaciones: '' },
        tuberias: { estado: '', observaciones: '' },
        drenaje: { estado: '', observaciones: '' },
        mueblesSanitarios: { estado: '', observaciones: '' },
        bombaHidraulica: { estado: '', observaciones: '' },
        tomaSiamesa: { estado: '', observaciones: '' },
        aljibeDeposito: { estado: '', observaciones: '' }
      },
      camposTexto: {}
    },
    sistemasVentilacion: {
      conceptos: {
        extractores: { estado: '', observaciones: '' },
        ventiladores: { estado: '', observaciones: '' },
        aireAcondicionado: { estado: '', observaciones: '' }
      },
      camposTexto: {}
    },
    poblacion: {
      conceptos: {
        estadoSalud: { estado: '', observaciones: '' },
        estadoPsicologico: { estado: '', observaciones: '' },
        trabajadoresDiscapacidad: { estado: '', observaciones: '' },
        adaptacionDiscapacidad: { estado: '', observaciones: '' }
      },
      camposTexto: {}
    },
    recipientesPresion: {
      conceptos: {
        tanquesHidroneumaticos: { estado: '', observaciones: '' },
        calderas: { estado: '', observaciones: '' },
        autotanques: { estado: '', observaciones: '' },
        evaporadoras: { estado: '', observaciones: '' },
        intercambiadorCalor: { estado: '', observaciones: '' },
        chillerCondensador: { estado: '', observaciones: '' },
        compresor: { estado: '', observaciones: '' },
        otro: { estado: '', observaciones: '' }
      },
      camposTexto: {
        folioNOM020: '', fechaEmision: '', unidadVerificacion: '',
        registroUnidad: '', observacionesGenerales: ''
      }
    },
    tanquesAlmacenamiento: {
      conceptos: {
        gasolina: { estado: '', observaciones: '' },
        diesel: { estado: '', observaciones: '' },
        gasLP: { estado: '', observaciones: '' },
        solventes: { estado: '', observaciones: '' },
        pinturas: { estado: '', observaciones: '' },
        amoniaco: { estado: '', observaciones: '' },
        gasNatural: { estado: '', observaciones: '' },
        aceiteVehicular: { estado: '', observaciones: '' }
      },
      camposTexto: {}
    }
  });

  const [expanded, setExpanded] = useState('estructural');
  const [pendingHighlightFieldKey, setPendingHighlightFieldKey] = useState(navHighlightFieldKey || null);

  // ============================================
  // INTEGRACIÓN CON BACKEND
  // ============================================

  /**
   * Cargar catálogo de sustancias peligrosas (para el selector de nombre de sustancia)
   */
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const response = await api.get('/hazardous-substances-catalog');
        setHazardousSubstancesCatalog(response.data || []);
      } catch (err) {
        // Sin señal (la petición nunca llegó a tener respuesta del servidor) vs. error real
        // (403/404/500 -- el servidor sí contestó): solo en el primer caso tiene sentido caer
        // al catálogo hardcodeado; un error real hay que verlo en consola tal cual, no
        // disfrazarlo de "estás sin conexión".
        if (!err.response) {
          console.warn('Sin conexión al cargar catálogo de sustancias peligrosas -- usando respaldo local.', err);
          setHazardousSubstancesCatalog(buildHazardousSubstancesCatalogFallback());
        } else {
          console.error('Error al cargar catálogo de sustancias peligrosas:', err);
        }
      }
    };
    fetchCatalog();
  }, []);

  /**
   * Toma los datos "de sitio" (propios + cotizados de referencia) que ya vienen
   * planos en la respuesta del backend (InspectionEvaluationDTO) y los vuelca al
   * estado editable + al estado de comparación.
   */
  const applySiteDataFromBackend = (data) => {
    const hasApproval = data.civilProtectionApproval === 'si';
    setSiteData({
      civilProtectionApproval: data.civilProtectionApproval || '',
      civilProtectionApprovalDate: hasApproval ? (data.civilProtectionApprovalDate || '') : '',
      civilProtectionApprovalValidity: hasApproval ? (data.civilProtectionApprovalValidity || '') : '',
      noHazardousSubstances: !!data.noHazardousSubstances,
      hazardousSubstances: data.hazardousSubstances || []
    });
    setQuotedSiteData({
      hazardousSubstances: data.quotedHazardousSubstances || []
    });
  };

  const handleSiteDataFieldChange = (field, value) => {
    setSiteData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'civilProtectionApproval' && value !== 'si') {
        next.civilProtectionApprovalDate = '';
        next.civilProtectionApprovalValidity = '';
      }
      return next;
    });
  };

  const handleAddHazardousSubstanceRow = () => {
    setSiteData(prev => ({
      ...prev,
      hazardousSubstances: [...prev.hazardousSubstances, { substanceName: '', quantity: '', unit: '' }]
    }));
  };

  const handleRemoveHazardousSubstanceRow = (index) => {
    setSiteData(prev => ({
      ...prev,
      hazardousSubstances: prev.hazardousSubstances.filter((_, i) => i !== index)
    }));
  };

  const handleHazardousSubstanceFieldChange = (index, field, value) => {
    setSiteData(prev => ({
      ...prev,
      hazardousSubstances: prev.hazardousSubstances.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const findQuotedSubstance = (name) => quotedSiteData.hazardousSubstances.find(
    q => (q.substanceName || '').toLowerCase() === (name || '').toLowerCase()
  );

  // Ente en pantalla, para que el aviso de borrador rechazado solo aparezca en el suyo
  const currentEntityRef = useRef({ quotationId, branchId: null });
  currentEntityRef.current = { quotationId, branchId: selectedEntity.branchId };

  // Arranca el motor de sincronización offline: evento 'online' + reintento periódico de
  // respaldo (ver offlineSyncEngine.js). Corre una sola vez por montaje de la pantalla, no por
  // ente -- un mismo borrador pendiente se sincroniza sin importar qué ente esté viendo ahorita
  // el inspector.
  useEffect(() => {
    const stopAutoSync = offlineSyncEngine.startAutoSync();
    const unsubscribe = offlineSyncEngine.subscribe((status) => {
      if (status.phase === 'auth-required') {
        setOfflineBanner('auth-required');
      } else if (status.phase === 'rejected'
        && String(status.detail?.quotationId) === String(currentEntityRef.current.quotationId)
        && String(status.detail?.branchId ?? 'MATRIZ') === String(currentEntityRef.current.branchId ?? 'MATRIZ')) {
        setSyncRejectedMessage(status.detail.syncRejected?.message || '');
        setSyncRejectedCode(status.detail.syncRejected?.code || null);
        setSyncRejectedDetails(status.detail.syncRejected?.details || null);
        setOfflineBanner('sync-rejected');
      } else if (status.phase === 'synced'
        && String(status.detail?.quotationId) === String(currentEntityRef.current.quotationId)
        && String(status.detail?.branchId ?? 'MATRIZ') === String(currentEntityRef.current.branchId ?? 'MATRIZ')) {
        // Se creó/actualizó en el servidor desde otro disparo del motor: la pantalla toma el id
        if (status.detail.evaluationId) setEvaluationId(status.detail.evaluationId);
        // Las sustancias heredadas que el motor unió al recuperar el borrador también entran a
        // la pantalla; si no, el siguiente autoguardado las volvería a borrar.
        if (status.detail.savedHazardousSubstances?.length) {
          setSiteData(prev => (prev.noHazardousSubstances ? prev : {
            ...prev,
            hazardousSubstances: offlineSyncEngine.mergeHazardousSubstances(prev.hazardousSubstances, status.detail.savedHazardousSubstances)
          }));
        }
        setOfflineBanner(prev => (prev === 'pending-sync' || prev === 'cold-start' ? null : prev));
      } else if (status.phase === 'idle' && offlineBanner === 'auth-required') {
        // Ya se resolvió (se volvió a iniciar sesión y sincronizó) -- el banner de
        // "pending-sync" (si aplica) se recalcula en el próximo ciclo de guardado local.
        setOfflineBanner(null);
      }
    });
    return () => { stopAutoSync(); unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cargar estado de entes y evaluación al montar el componente
   */
  // Copia síncrona del estado de entes: loadEvaluationForEntity la necesita (companyId) en el
  // mismo flujo en que se acaba de cargar, antes de que React vuelva a renderizar.
  const entitiesStatusRef = useRef(null);
  const applyEntitiesStatus = (entities) => {
    entitiesStatusRef.current = entities;
    setEntitiesStatus(entities);
  };
  // A) Autoguardado solo si hay cambios: se compara el payload contra el último sincronizado.
  // Antes se guardaba cada 30 s aunque nadie hubiera tocado nada (~1.6 s de servidor cada vez).
  const lastSyncedPayloadRef = useRef(null);
  const baselinePendingRef = useRef(false);
  // Un solo guardado a la vez: los demás esperan en fila y cada uno manda el estado más reciente
  // (antes autoguardado, guardado manual y sincronización se pisaban entre sí).
  const saveQueueRef = useRef(Promise.resolve());
  const queuedAutoSaveRef = useRef(false);
  const performSaveRef = useRef(null);

  useEffect(() => {
    const loadAsyncInspection = async () => {
      const record = await asyncInspectionStore.get(asyncId);
      if (!record) {
        setSnackbar({ open: true, message: 'Esta inspección sin ente ya no existe en este dispositivo (¿ya se asignó o descartó?).', severity: 'warning' });
        setLoading(false);
        return;
      }
      setAsyncMeta({ siteName: '', notes: '', gps: null, ...record.meta });
      if (record.data) {
        applySiteDataFromBackend(record.data);
        setEvaluaciones(inspectionEvaluationService.transformToFrontendFormat(record.data));
      }
      setLoading(false);
    };

    const loadEntitiesAndEvaluation = async () => {
      if (isAsync) {
        await loadAsyncInspection();
        return;
      }
      if (!quotationId) {
        setSnackbar({
          open: true,
          message: 'No se proporcionó ID de cotización',
          severity: 'warning'
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLoadingEntities(true);

        // B) Entes y evaluación del ente del link (si lo hay) se piden a la vez; antes iban en serie
        const entitiesPromise = inspectionEvaluationService.getEntitiesStatus(quotationId);
        entitiesPromise.catch(() => {});
        const prefetchBranchId = navEntityType === 'SUCURSAL' && navBranchId != null ? navBranchId
          : navEntityType === 'MATRIZ' ? null : undefined;
        let prefetched = null;
        if (prefetchBranchId !== undefined) {
          const promise = inspectionEvaluationService.getByEntity(quotationId, prefetchBranchId);
          promise.catch(() => {});
          prefetched = { branchId: prefetchBranchId, promise };
        }

        // 1. Cargar estado de entes (matriz + sucursales) -- el backend ya filtra "matriz"/
        // "branches" a solo los entes asignados al usuario actual cuando es Ejecutivo de
        // Operaciones (ver resolveRestrictToAssignedUserId en el controller)
        let entities = null;
        try {
          entities = await entitiesPromise;
          applyEntitiesStatus(entities);
        } catch (err) {
          console.warn('No se pudo cargar estado de entes, usando modo compatibilidad:', err);
        }

        // 2. Determinar el ente inicial según lo que el usuario tenga realmente asignado --
        // no asumir Matriz por default, porque un Ejecutivo de Operaciones puede no estar
        // asignado ahí (matriz llega null del backend en ese caso).
        let initialEntity = selectedEntity;
        if (!entities && offlineSyncEngine.isNetworkFailure) {
          // Sin señal: no se puede validar contra lo realmente asignado, pero si venimos de un
          // link directo (navBranchId/navEntityType) lo respetamos igual -- es mejor honrar la
          // intención de navegación que forzar Matriz a ciegas.
          if (navEntityType === 'SUCURSAL' && navBranchId != null) {
            initialEntity = { entityType: 'SUCURSAL', branchId: navBranchId, entityName: 'Sucursal' };
            setSelectedEntity(initialEntity);
          } else if (navEntityType === 'MATRIZ') {
            initialEntity = { entityType: 'MATRIZ', branchId: null, entityName: 'Matriz' };
            setSelectedEntity(initialEntity);
          }
          // si tampoco hay navEntityType, se queda con el default (Matriz) ya en selectedEntity.
        }
        if (entities) {
          // Si venimos de "Validación de Campos" con un ente específico a resaltar, y ese
          // ente sí está entre los asignados al usuario, respetarlo en vez del default.
          const navEntityFromLink = navEntityType === 'MATRIZ' && entities.matriz
            ? { entityType: 'MATRIZ', branchId: null, entityName: entities.matriz.entityName || 'Matriz' }
            : navEntityType === 'SUCURSAL'
              ? entities.branches?.find(b => String(b.branchId) === String(navBranchId))
              : null;
          const navEntity = navEntityFromLink && navEntityType === 'SUCURSAL'
            ? { entityType: 'SUCURSAL', branchId: navEntityFromLink.branchId, entityName: navEntityFromLink.entityName }
            : navEntityFromLink;

          if (navEntity) {
            initialEntity = navEntity;
          } else if (entities.matriz) {
            initialEntity = { entityType: 'MATRIZ', branchId: null, entityName: entities.matriz.entityName || 'Matriz' };
          } else if (entities.branches?.length > 0) {
            const firstBranch = entities.branches[0];
            initialEntity = { entityType: 'SUCURSAL', branchId: firstBranch.branchId, entityName: firstBranch.entityName };
          } else {
            setSnackbar({ open: true, message: 'No tienes ningún ente asignado en esta orden de trabajo', severity: 'warning' });
            setLoading(false);
            setLoadingEntities(false);
            return;
          }
          setSelectedEntity(initialEntity);
        }

        // 3. Cargar evaluación para el ente inicial
        await loadEvaluationForEntity(initialEntity.branchId,
          prefetched && String(prefetched.branchId) === String(initialEntity.branchId ?? null) ? prefetched.promise : null);

      } catch (error) {
        console.error('Error al cargar evaluación:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar evaluación: ' + (error.response?.data?.error || error.message),
          severity: 'error'
        });
      } finally {
        setLoading(false);
        setLoadingEntities(false);
      }
    };

    loadEntitiesAndEvaluation();
  }, [quotationId, asyncId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refresca el estado de entes (matriz + sucursales) del selector -- necesario después de
   * guardar/completar/clonar para que el chip de cada ente ("Sin evaluar" / "X% completado")
   * deje de mostrar el dato viejo.
   */
  const refreshEntitiesStatus = async () => {
    try {
      const updatedEntities = await inspectionEvaluationService.getEntitiesStatus(quotationId);
      applyEntitiesStatus(updatedEntities);
    } catch (err) {
      console.warn('No se pudo refrescar estado de entes:', err);
    }
  };

  /**
   * Cargar evaluación para un ente específico
   */
  // "no existe evaluación para este ente" llega como 404 con ese mensaje (ver controller)
  const isEvaluationNotFound = (err) => err?.response?.status === 404
    && String(err.response?.data?.error || '').includes('No existe evaluación');

  const loadEvaluationForEntity = async (branchId, prefetchedPromise = null) => {
    try {
      lastSyncedPayloadRef.current = null;
      baselinePendingRef.current = false;
      // Al cambiar de ente se descarta el "pendiente de confirmar" del ente anterior --
      // cada ente lleva su propio set de conceptos auto-marcados.
      setAutoMarkedConcepts(new Set());

      // Offline-first: si hay un borrador local para este ente, usarlo de entrada sin tocar la
      // red -- cubre tanto "sigo trabajando sin señal" como "recargué la página por accidente"
      // (ver plan "Captura offline de Evaluación de Inspección"). No se sobreescribe con un
      // fetch nuevo: el borrador local es la copia de trabajo mientras tenga cambios sin
      // sincronizar, para no perderlos.
      const localDraft = await offlineEvaluationStore.getDraft(quotationId, branchId);
      if (localDraft) {
        setEvaluationId(localDraft.evaluationId || null);
        setEvaluationInfo(localDraft.data);
        applySiteDataFromBackend(localDraft.data);
        setEvaluaciones(inspectionEvaluationService.transformToFrontendFormat(localDraft.data));
        setSyncRejectedMessage(localDraft.syncRejected?.message || '');
        setSyncRejectedCode(localDraft.syncRejected?.code || null);
        setSyncRejectedDetails(localDraft.syncRejected?.details || null);
        setOfflineBanner(localDraft.syncRejected
          ? 'sync-rejected'
          : localDraft.pendingSync
            ? (localDraft.startedFromServerSnapshot ? 'pending-sync' : 'cold-start')
            : null);
        setSnackbar({
          open: true,
          message: localDraft.pendingSync
            ? 'Cargado desde el borrador guardado en este dispositivo (aún sin sincronizar).'
            : 'Evaluación cargada.',
          severity: 'info'
        });
        return;
      }

      // Una sola llamada: trae la evaluación del ente o 404 si todavía no existe
      let data = null;
      let exists;
      try {
        data = await (prefetchedPromise || inspectionEvaluationService.getByEntity(quotationId, branchId));
        exists = true;
      } catch (err) {
        if (isEvaluationNotFound(err)) {
          exists = false;
        } else if (!offlineSyncEngine.isNetworkFailure(err)) {
          throw err;
        }
      }
      if (exists === undefined && !OFFLINE_FIELD_MODE) {
        // Sin captura en el dispositivo: sin señal no se abre (se usa el formato en papel)
        setEvaluationId(null);
        setEvaluationInfo(null);
        setSnackbar({
          open: true,
          message: 'Sin conexión: no se pudo abrir la evaluación. Usa el formato en papel y captúrala cuando tengas señal.',
          severity: 'error'
        });
        return;
      }
      if (exists === undefined) {

        // Sin señal y sin borrador local: primera vez que se abre este ente sin conexión --
        // arranca en blanco. Esto reemplazará lo que exista en el servidor al sincronizar
        // (regla ya acordada: siempre gana quien tiene la OT asignada).
        setEvaluationId(null);
        setEvaluationInfo(null);
        const emptySiteData = {
          civilProtectionApproval: '',
          civilProtectionApprovalDate: '',
          civilProtectionApprovalValidity: '',
          noHazardousSubstances: false,
          hazardousSubstances: []
        };
        setSiteData(emptySiteData);
        setQuotedSiteData({ hazardousSubstances: [] });
        setEvaluaciones(inspectionEvaluationService.transformToFrontendFormat({}));
        setOfflineBanner('cold-start');

        const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
        await offlineEvaluationStore.saveDraft(quotationId, branchId, {
          _createPayload: {
            companyId: navCompanyId || null,
            evaluatorName: userInfo.name || userInfo.username,
            createdBy: userInfo.id
          },
          status: 'IN_PROGRESS',
          concepts: [],
          textFields: {},
          civilProtectionApproval: null,
          civilProtectionApprovalDate: null,
          civilProtectionApprovalValidity: null,
          hazardousSubstances: []
        }, { pendingSync: false, startedFromServerSnapshot: false });

        setSnackbar({
          open: true,
          message: 'Sin conexión: capturando esta evaluación desde cero en este dispositivo.',
          severity: 'warning'
        });
        return;
      }

      if (exists) {
        // Cargar evaluación existente (ya llegó arriba)
        baselinePendingRef.current = true;
        setEvaluationId(data.id);
        setEvaluationInfo(data);
        applySiteDataFromBackend(data);

        // Transformar datos del backend al formato del frontend
        const frontendData = inspectionEvaluationService.transformToFrontendFormat(data);
        setEvaluaciones(frontendData);
        setOfflineBanner(null);

        // Cachea la copia real del servidor como punto de partida -- si se pierde la señal
        // después, hay algo de qué recuperar en vez de arrancar en blanco.
        await offlineEvaluationStore.saveDraft(quotationId, branchId, data, {
          evaluationId: data.id, pendingSync: false, startedFromServerSnapshot: true,
          serverUpdatedAt: data.updatedAt || null, serverVersion: data.version ?? null, forceOverwrite: false
        });

        setSnackbar({
          open: true,
          message: `Evaluación cargada: ${data.progressPercentage?.toFixed(1)}% completado`,
          severity: 'success'
        });
      } else {
        // Crear nueva evaluación para este ente
        const knownEntities = entitiesStatusRef.current;
        let companyIdForCreate = navCompanyId
          || knownEntities?.matriz?.companyId
          || knownEntities?.branches?.find(b => b.companyId)?.companyId;
        if (!companyIdForCreate) {
          const quotationResponse = await api.get(`/quotations/${quotationId}`);
          const quotation = quotationResponse.data;
          companyIdForCreate = quotation.company?.id || quotation.companyId;
        }

        const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
        const newEvaluation = await inspectionEvaluationService.createFromQuotation(
          quotationId,
          {
            companyId: companyIdForCreate,
            branchId: branchId,
            evaluatorName: userInfo.name || userInfo.username,
            createdBy: userInfo.id
          }
        );

        baselinePendingRef.current = true;
        setEvaluationId(newEvaluation.id);
        setEvaluationInfo(newEvaluation);
        applySiteDataFromBackend(newEvaluation);

        // Resetear evaluaciones al estado vacío
        const emptyState = inspectionEvaluationService.transformToFrontendFormat({});
        setEvaluaciones(emptyState);
        setOfflineBanner(null);

        await offlineEvaluationStore.saveDraft(quotationId, branchId, newEvaluation, {
          evaluationId: newEvaluation.id, pendingSync: false, startedFromServerSnapshot: true,
          serverUpdatedAt: newEvaluation.updatedAt || null, serverVersion: newEvaluation.version ?? null, forceOverwrite: false
        });

        setSnackbar({
          open: true,
          message: 'Nueva evaluación creada para este ente',
          severity: 'info'
        });

        const current = entitiesStatusRef.current;
        if (current) {
          const markCreated = (entity) => (entity && String(entity.branchId ?? null) === String(branchId ?? null)
            ? { ...entity, hasEvaluation: true, evaluationId: newEvaluation.id, status: newEvaluation.status, progressPercentage: 0 }
            : entity);
          applyEntitiesStatus({ ...current, matriz: markCreated(current.matriz), branches: (current.branches || []).map(markCreated) });
        } else {
          await refreshEntitiesStatus();
        }
      }
    } catch (error) {
      console.error('Error al cargar evaluación del ente:', error);
      throw error;
    }
  };

  /**
   * Cambiar ente seleccionado
   */
  const handleEntityChange = async (entity) => {
    if (entity.branchId === selectedEntity.branchId && entity.entityType === selectedEntity.entityType) return;

    try {
      setLoading(true);
      setSelectedEntity(entity);
      await loadEvaluationForEntity(entity.branchId);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al cambiar ente: ' + (error.response?.data?.error || error.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clonar evaluación a otros entes
   */
  const handleClone = async () => {
    if (!evaluationId || cloneTargets.length === 0) return;

    try {
      setCloning(true);
      const cloned = await inspectionEvaluationService.cloneEvaluation(evaluationId, cloneTargets);

      await refreshEntitiesStatus();

      setCloneDialogOpen(false);
      setCloneTargets([]);
      setSnackbar({
        open: true,
        message: `Evaluación clonada exitosamente a ${cloned.length} ente(s)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al clonar:', error);
      setSnackbar({
        open: true,
        message: 'Error al clonar: ' + (error.response?.data?.error || error.message),
        severity: 'error'
      });
    } finally {
      setCloning(false);
    }
  };

  const handleToggleCloneTarget = (branchId) => {
    setCloneTargets(prev =>
      prev.includes(branchId) ? prev.filter(id => id !== branchId) : [...prev, branchId]
    );
  };

  /**
   * Autoguardado cada 30 segundos -- ya NO exige evaluationId: si todavía no existe (caso
   * "primera vez, en sitio, sin señal"), igual escribe al borrador local; el intento contra el
   * servidor simplemente no aplica hasta que haya un id (se resuelve solo al sincronizar).
   */
  useEffect(() => {
    if (!autoSaveEnabled) return;

    const interval = setInterval(() => {
      handleSave(true); // true = autoguardado silencioso
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
    // siteData debe estar en las dependencias: handleSave la captura por closure, y sin
    // esto el intervalo queda con una copia vieja -- si agregas una sustancia peligrosa
    // (o cualquier cambio en siteData) sin que `evaluaciones` también cambie, el siguiente
    // autoguardado manda la versión vieja (sin tu cambio) y luego applySiteDataFromBackend()
    // pisa el estado actual con esa respuesta vieja, borrando lo que acabas de escribir.
  }, [evaluationId, evaluaciones, autoSaveEnabled, siteData]);

  /**
   * Guardar evaluación. Primero SIEMPRE al borrador local (IndexedDB, nunca depende de red);
   * después intenta contra el servidor. Si falla por falta de señal, se queda marcado
   * pendingSync=true para que offlineSyncEngine lo recoja solo al reconectar -- no es un
   * error, es el flujo esperado en campo.
   */
  const buildBackendPayload = () => {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    return inspectionEvaluationService.transformToBackendFormat(
      evaluaciones,
      evaluationId,
      quotationId,
      evaluationInfo?.companyId || navCompanyId || userInfo.company?.id,
      userInfo.id || evaluationInfo?.createdBy,
      selectedEntity.branchId,
      siteData
    );
  };

  // Punto de partida del autoguardado: lo recién cargado del servidor cuenta como "ya guardado"
  useEffect(() => {
    if (!baselinePendingRef.current || !evaluationId) return;
    baselinePendingRef.current = false;
    lastSyncedPayloadRef.current = JSON.stringify(buildBackendPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationId, evaluaciones, siteData]);

  const handleSave = (isAutoSave = false) => {
    if (isAutoSave && queuedAutoSaveRef.current) return saveQueueRef.current;
    if (isAutoSave) queuedAutoSaveRef.current = true;
    const run = saveQueueRef.current
      .then(() => offlineSyncEngine.waitForIdle())
      .then(() => {
        if (isAutoSave) queuedAutoSaveRef.current = false;
        return performSaveRef.current(isAutoSave);
      });
    saveQueueRef.current = run.catch(() => {});
    return run;
  };

  const performSave = async (isAutoSave = false) => {
    // Una fila de sustancia peligrosa con nombre pero sin cantidad/unidad se descarta
    // silenciosamente al enviarla al backend (ambos campos son NOT NULL ahí) -- sin este
    // aviso, el usuario cree que guardó pero la fila desaparece sin explicación.
    const incompleteSubstanceRows = (siteData.hazardousSubstances || []).filter(
      item => item.substanceName && (item.quantity === '' || item.quantity == null || !item.unit)
    );
    if (!isAutoSave && incompleteSubstanceRows.length > 0) {
      setSnackbar({
        open: true,
        message: `Completa cantidad y unidad de: ${incompleteSubstanceRows.map(s => s.substanceName).join(', ')} (si no, no se guardan).`,
        severity: 'warning'
      });
      return false;
    }

    if (isAsync) {
      await asyncInspectionStore.save(asyncId, { data: buildBackendPayload(), meta: asyncMeta });
      setLastSaved(new Date());
      if (!isAutoSave) {
        setSnackbar({ open: true, message: 'Guardada en este dispositivo. Asígnala a su ente desde Programas cuando tengas señal.', severity: 'info' });
      }
      return;
    }

    const branchId = selectedEntity.branchId;
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const companyId = evaluationInfo?.companyId || navCompanyId || userInfo.company?.id;
    const createdBy = userInfo.id || evaluationInfo?.createdBy;
    const backendData = buildBackendPayload();
    const payloadKey = JSON.stringify(backendData);
    if (isAutoSave && evaluationId && payloadKey === lastSyncedPayloadRef.current) {
      return; // sin cambios desde el último guardado
    }

    // 1. Local primero, siempre funciona -- conserva el _createPayload que ya traía el
    // borrador (metadata para crear la evaluación cuando se sincronice), si existía.
    const existingLocalDraft = await offlineEvaluationStore.getDraft(quotationId, branchId);
    await offlineEvaluationStore.saveDraft(quotationId, branchId, {
      ...backendData,
      _createPayload: existingLocalDraft?.data?._createPayload || {
        companyId: companyId || null, evaluatorName: userInfo.name || userInfo.username, createdBy
      }
    }, { evaluationId, pendingSync: true, startedFromServerSnapshot: existingLocalDraft?.startedFromServerSnapshot });

    if (existingLocalDraft?.syncRejected && ['CONFLICT', 'EVALUATION_NOT_FOUND'].includes(existingLocalDraft.syncRejected.code)) {
      // Pendiente de que el inspector elija en el banner: se guarda en el dispositivo, sin subir
      await offlineEvaluationStore.markSyncRejected(quotationId, branchId, existingLocalDraft.syncRejected);
      setLastSaved(new Date());
      return;
    }

    if (!evaluationId) {
      // Sin id todavía (cold-start offline): el borrador local ya quedó guardado arriba: eso
      // es "guardar" en este momento. Se crea de verdad en el servidor al sincronizar.
      setLastSaved(new Date());
      if (!isAutoSave) {
        setSnackbar({ open: true, message: 'Guardado en este dispositivo (sin conexión) -- se sincroniza solo al recuperar señal.', severity: 'info' });
      }
      return;
    }

    try {
      setSaving(true);

      // 2. Si había cambios hechos sin señal, revisar que nadie más la haya guardado mientras
      await offlineSyncEngine.checkServerConflict(existingLocalDraft?.pendingSync ? existingLocalDraft : null);

      // 3. Intento de subida al servidor (mismo payload local)
      const savedData = await inspectionEvaluationService.save(backendData);
      lastSyncedPayloadRef.current = payloadKey;

      setEvaluationInfo(savedData);
      applySiteDataFromBackend(savedData);
      setLastSaved(new Date());
      await offlineEvaluationStore.saveDraft(quotationId, branchId, backendData, {
        evaluationId: savedData.id, pendingSync: false, startedFromServerSnapshot: true,
        serverUpdatedAt: savedData.updatedAt || null, serverVersion: savedData.version ?? null, forceOverwrite: false
      });
      if (offlineBanner === 'pending-sync' || offlineBanner === 'cold-start') {
        setOfflineBanner(null);
      }

      if (!isAutoSave) {
        // Solo en guardado manual -- el chip del selector de ente ("Sin evaluar" / "X% completado")
        // se queda con el dato viejo si no se refresca después de guardar.
        await refreshEntitiesStatus();
        setSnackbar({
          open: true,
          message: `Evaluación guardada: ${savedData.progressPercentage?.toFixed(1)}% completado`,
          severity: 'success'
        });
      }
    } catch (error) {
      if (!OFFLINE_FIELD_MODE && (offlineSyncEngine.isNetworkFailure(error)
        || ['CONFLICT', 'EVALUATION_NOT_FOUND'].includes(error.response?.data?.code))) {
        setSnackbar({
          open: true,
          message: offlineSyncEngine.isNetworkFailure(error)
            ? 'Sin conexión: los cambios NO se guardaron. Revisa tu señal e inténtalo de nuevo.'
            : `${error.response.data.error} Recarga la pantalla.`,
          severity: 'error'
        });
      } else if (offlineSyncEngine.isNetworkFailure(error)) {
        // Sin señal: el borrador local YA quedó guardado arriba (paso 1) con pendingSync=true
        // -- no es un error, offlineSyncEngine lo sincroniza solo al reconectar.
        setOfflineBanner(prev => prev === 'auth-required' ? prev : 'pending-sync');
        if (!isAutoSave) {
          setSnackbar({ open: true, message: 'Sin conexión: guardado en este dispositivo, se sincroniza solo al recuperar señal.', severity: 'warning' });
        }
      } else if (['CONFLICT', 'EVALUATION_NOT_FOUND'].includes(error.response?.data?.code)) {
        const rejection = {
          status: error.response.status, message: error.response.data.error,
          code: error.response.data.code, details: error.response.data.details || null
        };
        await offlineEvaluationStore.markSyncRejected(quotationId, branchId, rejection);
        setSyncRejectedMessage(rejection.message);
        setSyncRejectedCode(rejection.code);
        setSyncRejectedDetails(rejection.details);
        setOfflineBanner('sync-rejected');
      } else {
        console.error('Error al guardar evaluación:', error);
        setSnackbar({
          open: true,
          message: 'Error al guardar: ' + (error.response?.data?.error || error.message),
          severity: 'error'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  performSaveRef.current = performSave;

  useEffect(() => {
    if (offlineBanner !== 'sync-rejected') { setDraftInfo(null); return; }
    let active = true;
    offlineEvaluationStore.getDraft(quotationId, selectedEntity.branchId).then((draft) => {
      if (!active || !draft) return;
      setDraftInfo({
        at: draft.lastLocalChangeAt ? new Date(draft.lastLocalChangeAt) : null,
        concepts: (draft.data?.concepts || []).length,
        substances: (draft.data?.hazardousSubstances || []).length
      });
    });
    return () => { active = false; };
  }, [offlineBanner, quotationId, selectedEntity.branchId]);

  const conflictDetailText = (() => {
    const d = syncRejectedDetails;
    if (!d) return '';
    const who = d.serverUpdatedByName || 'Otra persona';
    const when = d.serverUpdatedAt
      ? ` el ${new Date(d.serverUpdatedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}` : '';
    const parts = [];
    if (d.diff?.concepts) parts.push(`${d.diff.concepts} concepto${d.diff.concepts === 1 ? '' : 's'}`);
    if (d.diff?.textFields) parts.push(`${d.diff.textFields} campo${d.diff.textFields === 1 ? '' : 's'} de texto`);
    if (d.diff?.substances) parts.push(`${d.diff.substances} sustancia${d.diff.substances === 1 ? '' : 's'}`);
    if (d.diff?.siteData) parts.push('datos del Visto Bueno');
    return ` ${who} la guardó${when}.${parts.length ? ` Difiere de tu borrador en: ${parts.join(', ')}.` : ''}`;
  })();

  const draftInfoText = draftInfo
    ? ` Borrador de este dispositivo${draftInfo.at ? ` del ${draftInfo.at.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}` : ''}: ${draftInfo.concepts} conceptos evaluados, ${draftInfo.substances} sustancias.`
    : '';

  // Acciones del banner de borrador rechazado (conflicto / evaluación borrada en el servidor)
  const resolveRejectedDraft = async (action) => {
    const branchId = selectedEntity.branchId;
    const draft = await offlineEvaluationStore.getDraft(quotationId, branchId);
    if (!draft && action !== 'discard') return;
    try {
      setLoading(true);
      if (action === 'save-as-new') {
        await offlineEvaluationStore.saveDraft(quotationId, branchId, { ...draft.data, id: null }, {
          resetEvaluationId: true, pendingSync: true, startedFromServerSnapshot: false, forceOverwrite: false
        });
        setEvaluationId(null);
        setOfflineBanner('pending-sync');
        offlineSyncEngine.syncAllPending();
      } else if (action === 'keep-mine') {
        await offlineEvaluationStore.saveDraft(quotationId, branchId, draft.data, { pendingSync: true, forceOverwrite: true });
        setOfflineBanner('pending-sync');
        offlineSyncEngine.syncAllPending();
      } else {
        // 'use-server' / 'discard': se borra la copia del dispositivo y se recarga del servidor
        await offlineEvaluationStore.clearDraft(quotationId, branchId);
        setOfflineBanner(null);
        await loadEvaluationForEntity(branchId);
      }
      setSyncRejectedCode(null);
      setSyncRejectedDetails(null);
      setSyncRejectedMessage('');
    } catch (error) {
      setSnackbar({ open: true, message: 'No se pudo completar la acción: ' + (error.response?.data?.error || error.message), severity: 'error' });
    } finally {
      setLoading(false);
      setDiscardDraftDialogOpen(false);
    }
  };

  const captureGps = () => {
    if (!navigator.geolocation) {
      setSnackbar({ open: true, message: 'Este dispositivo no permite obtener la ubicación.', severity: 'warning' });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const gps = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy), at: new Date().toISOString() };
        setAsyncMeta(prev => ({ ...prev, gps }));
        asyncInspectionStore.save(asyncId, { meta: { gps } });
        setLocating(false);
      },
      () => {
        setLocating(false);
        setSnackbar({ open: true, message: 'No se pudo obtener la ubicación. Revisa que el GPS y el permiso de ubicación estén activos.', severity: 'warning' });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
    );
  };

  const updateAsyncMeta = (field, value) => {
    setAsyncMeta(prev => ({ ...prev, [field]: value }));
    asyncInspectionStore.save(asyncId, { meta: { [field]: value } });
  };

  /**
   * Completar evaluación
   */
  const handleComplete = async () => {
    if (!evaluationId) return;

    // Candado en el frontend para los 2 campos "de sitio" restantes (Acordeón 10): no son parte
    // del conteo de 64 conceptos que valida el backend (viven como columnas propias de la
    // evaluación, no como filas de concepto), así que sin este chequeo se podía completar con
    // el checklist al 100% y estos todavía vacíos. Bloquea con warning -- a diferencia de los
    // conceptos del checklist (más abajo), estos son obligatorios siempre.
    // "Capacidad de Almacenamiento de Gas" ya no se valida aparte: se calcula sumando las filas
    // "Gas LP" de Sustancias Peligrosas (ver DocumentService.resolveCapacidadGas), así que
    // validar esa lista ya cubre el caso.
    const missingSiteFields = [];
    if (!siteData.noHazardousSubstances && (!siteData.hazardousSubstances || siteData.hazardousSubstances.length === 0)) {
      missingSiteFields.push('Sustancias Peligrosas Presentes (o marca "No aplica")');
    }
    if (!siteData.civilProtectionApproval) {
      missingSiteFields.push('Visto Bueno de Protección Civil');
    }
    if (missingSiteFields.length > 0) {
      setCompleteIssueDialog({
        severity: 'warning',
        title: 'No se puede completar la evaluación',
        message: (
          <>
            Faltan los siguientes campos obligatorios de la sección <strong>Sustancias Peligrosas, Gas y Protección Civil</strong>:
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {missingSiteFields.map(f => <li key={f}><strong>{f}</strong></li>)}
            </Box>
          </>
        )
      });
      return;
    }

    // Recordatorio (no es un campo "de sitio", es el checklist normal) -- mismo conteo que ya
    // valida el backend (TOTAL_CONCEPTOS_ESPERADOS = 64), calculado acá para avisar antes de
    // intentar en vez de que el usuario descubra el rechazo después de guardar.
    // Se cuenta contra la lista canónica (SECTION_CONCEPT_KEYS): evaluaciones[seccion].conceptos
    // solo trae lo ya tocado, así que contar sus llaves dejaba pasar los conceptos nunca abiertos.
    const pendingBySection = Object.entries(SECTION_CONCEPT_KEYS)
      .map(([seccion, keys]) => [seccion, keys.filter(k => !evaluaciones[seccion]?.conceptos?.[k]?.estado).length])
      .filter(([, n]) => n > 0);
    const pendingConcepts = pendingBySection.reduce((sum, [, n]) => sum + n, 0);
    if (pendingConcepts > 0) {
      setCompleteIssueDialog({
        severity: 'warning',
        title: 'No se puede completar la evaluación',
        message: (
          <>
            Todavía faltan <strong>{pendingConcepts}</strong> concepto{pendingConcepts > 1 ? 's' : ''} del checklist por evaluar:
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {pendingBySection.map(([seccion, n]) => (
                <li key={seccion}><strong>{SECTION_LABELS[seccion] || seccion}</strong>: {n}</li>
              ))}
            </Box>
          </>
        )
      });
      return;
    }

    try {
      setSaving(true);

      // Primero guardar cambios actuales
      await handleSave(true);
      
      // Luego cambiar estado a COMPLETED
      const updatedData = await inspectionEvaluationService.updateStatus(
        evaluationId,
        'COMPLETED'
      );
      
      setEvaluationInfo(updatedData);
      applySiteDataFromBackend(updatedData);
      // El cambio de estado sube la versión en el sistema: el borrador debe apuntar a la nueva,
      // o el siguiente guardado fallido por señal se marcaría como conflicto sin serlo.
      const draftAfterComplete = await offlineEvaluationStore.getDraft(quotationId, selectedEntity.branchId);
      await offlineEvaluationStore.saveDraft(quotationId, selectedEntity.branchId, draftAfterComplete?.data || buildBackendPayload(), {
        evaluationId: updatedData.id, pendingSync: false, startedFromServerSnapshot: true,
        serverUpdatedAt: updatedData.updatedAt || null, serverVersion: updatedData.version ?? null, forceOverwrite: false
      });

      // El chip del ente en "Seleccionar Ente a Evaluar" debe reflejar que ya quedó completada,
      // no seguir mostrando "Sin evaluar" con el dato de antes de completar.
      await refreshEntitiesStatus();

      setSnackbar({
        open: true,
        message: '¡Evaluación completada exitosamente!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al completar evaluación:', error);
      setSnackbar({
        open: true,
        message: 'Error al completar: ' + (error.response?.data?.error || error.message),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // A qué acordeón pertenece cada campo saltable desde "Validación de Campos" -- Sustancias
  // Peligrosas/Capacidad de Gas/Visto Bueno de PC viven en el Acordeón 10 (sustanciasGasPC),
  // Trabajadores con Discapacidad vive en el Acordeón de Población (poblacion).
  const HIGHLIGHT_FIELD_ACCORDION = {
    SUSTANCIAS_PELIGROSAS: 'sustanciasGasPC',
    CAPACIDAD_GAS: 'sustanciasGasPC',
    vb: 'sustanciasGasPC',
    TRABAJADORES_DISCAPACIDAD: 'poblacion',
  };

  // Si llegamos desde "Validación de Campos" con un campo específico a resaltar, una vez cargada
  // la evaluación del ente correcto, abrimos el acordeón correspondiente y desplazamos/resaltamos
  // ese campo puntual.
  useEffect(() => {
    if (!evaluationId || !pendingHighlightFieldKey) return;
    const key = pendingHighlightFieldKey;
    // Dictámenes ({SECCION_campo}): se abre la sección de esa evaluación
    const DICTAMEN_ACCORDION = { ESTRUCTURAL: 'estructural', EQUIPO_ESPECIFICO: 'equipoEspecifico',
      INSTALACION_ELECTRICA: 'instalacionElectrica', INSTALACION_GAS: 'instalacionGas', RECIPIENTES_PRESION: 'recipientesPresion' };
    const dictamenSection = Object.keys(DICTAMEN_ACCORDION).find(prefix => key.startsWith(`${prefix}_`));
    setExpanded(HIGHLIGHT_FIELD_ACCORDION[key] || (dictamenSection ? DICTAMEN_ACCORDION[dictamenSection] : 'sustanciasGasPC'));
    const timer = setTimeout(() => {
      const el = document.getElementById(`field-${key}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = el.closest('.MuiFormControl-root') || el;
        target.style.transition = 'box-shadow 0.3s ease';
        target.style.boxShadow = '0 0 0 3px #ff9800';
        target.style.borderRadius = target.style.borderRadius || '8px';
        setTimeout(() => { target.style.boxShadow = ''; }, 2500);
      }
      setPendingHighlightFieldKey(null);
    }, 500);
    return () => clearTimeout(timer);
  }, [evaluationId, pendingHighlightFieldKey]);

  // Etiqueta del chip de cada ente en "Seleccionar Ente a Evaluar" -- distingue una evaluación
  // ya completada/aprobada de una que solo está en progreso al mismo % (ej. 100% recién marcado
  // con "Marcar todos como Bueno" pero sin pasar por Completar Evaluación todavía).
  const entityStatusLabel = (entity) => {
    if (!entity?.hasEvaluation) return 'Sin evaluar';
    if (entity.status === 'COMPLETED' || entity.status === 'APPROVED') return 'Completada ✓';
    return `${entity.progressPercentage?.toFixed(0)}% completado`;
  };

  const handleEstadoChange = (seccion, concepto, nuevoEstado) => {
    setEvaluaciones(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        conceptos: {
          ...prev[seccion].conceptos,
          [concepto]: { ...prev[seccion].conceptos[concepto], estado: nuevoEstado }
        }
      }
    }));
    // Tocar el toggle de un concepto auto-marcado cuenta como revisión real -- se quita
    // la marca de "pendiente de confirmar".
    setAutoMarkedConcepts(prev => {
      const key = `${seccion}.${concepto}`;
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Conceptos que usan escala Sí/No/N-A (renderYesNoRow) en vez de Bueno/Regular/Malo/N-A --
  // "Marcar todos como Bueno" no debe tocarlos porque 'bueno' no es un valor válido ahí.
  const YES_NO_CONCEPT_KEYS = new Set(['poblacion.trabajadoresDiscapacidad']);
  // Fila opcional para equipos presentes que no están en la lista: no cuenta para el avance
  // (ni aquí ni en el backend, InspectionConceptRepository) ni la toca "Marcar todos como Bueno".
  const OTRO_EQUIPO_KEY = 'otroEquipo';
  const OTRO_EQUIPO_LABEL = 'Otro equipo presente (opcional: elige estado y especifícalo en observaciones)';

  // Lista canónica de conceptos por sección -- necesaria porque evaluaciones[seccion].conceptos
  // llega "disperso" (initializeEmptyState() arranca en {} y el backend tampoco guarda fila
  // para lo que el usuario aún no capturó), así que Object.keys(conceptos) NO alcanza para saber
  // cuántos conceptos hay en total; hay que listarlos explícitamente, igual que se listan al
  // renderizar cada tabla.
  const SECTION_CONCEPT_KEYS = {
    estructural: ['ventanales', 'anunciosVolados', 'anunciosPanorámicos', 'marquesinas', 'acabadosFachada', 'pretilesBalcones', 'inclinacionInmueble', 'cimentacion', 'columnas', 'muros', 'bardasMallas', 'escaleras', 'arboles', 'rampasAuto', 'pasosDesnivel', 'estacionamiento'],
    equipoEspecifico: ['bombas', 'moduloDespacho', 'parosEmergencia', 'mangueraDescarga', 'montacargas'],
    instalacionElectrica: ['transformadorElectrico', 'subestacionElectrica', 'postesDistribucion', 'tablerosElectricos', 'equiposComputo', 'socketsFocos', 'tomaCorrientes', 'plantaEmergencia', 'aireAcondicionado'],
    instalacionGas: ['tuberiaGas', 'tanqueMovil', 'tanqueEstacionario', 'valvulasTanque'],
    instalacionHidrosanitaria: ['cisterna', 'tuberias', 'drenaje', 'mueblesSanitarios', 'bombaHidraulica', 'tomaSiamesa', 'aljibeDeposito'],
    sistemasVentilacion: ['extractores', 'ventiladores', 'aireAcondicionado'],
    poblacion: ['estadoSalud', 'estadoPsicologico', 'trabajadoresDiscapacidad', 'adaptacionDiscapacidad'],
    recipientesPresion: ['tanquesHidroneumaticos', 'calderas', 'autotanques', 'evaporadoras', 'intercambiadorCalor', 'chillerCondensador', 'compresor', 'otro'],
    tanquesAlmacenamiento: ['gasolina', 'diesel', 'gasLP', 'solventes', 'pinturas', 'amoniaco', 'gasNatural', 'aceiteVehicular']
  };

  const SECTION_LABELS = {
    estructural: 'Estructural',
    equipoEspecifico: 'Equipo específico',
    instalacionElectrica: 'Instalación eléctrica',
    instalacionGas: 'Instalación de gas',
    instalacionHidrosanitaria: 'Instalación hidrosanitaria',
    sistemasVentilacion: 'Sistemas de ventilación',
    poblacion: 'Población',
    recipientesPresion: 'Recipientes a presión',
    tanquesAlmacenamiento: 'Tanques de almacenamiento'
  };

  const confirmAutoMarked = (seccion, concepto) => {
    setAutoMarkedConcepts(prev => {
      const next = new Set(prev);
      next.delete(`${seccion}.${concepto}`);
      return next;
    });
  };

  // Cuenta conceptos vacíos DENTRO de una sola sección (para el botón junto a cada tabla)
  const countPendingInSection = (seccion) => {
    const conceptos = evaluaciones[seccion]?.conceptos || {};
    const keys = SECTION_CONCEPT_KEYS[seccion] || Object.keys(conceptos);
    return keys.filter(key => {
      const fullKey = `${seccion}.${key}`;
      const estado = conceptos[key]?.estado ?? '';
      return !YES_NO_CONCEPT_KEYS.has(fullKey) && estado === '';
    }).length;
  };

  // Rellena SOLO los conceptos vacíos de UNA sección (nunca sobreescribe lo ya capturado)
  // con "Bueno" y los deja marcados como pendientes de confirmar -- ver autoMarkedConcepts.
  const handleMarkSectionGood = (seccion) => {
    if (!seccion) return;
    const newlyMarked = new Set(autoMarkedConcepts);
    setEvaluaciones(prev => {
      const conceptos = { ...prev[seccion].conceptos };
      const keys = SECTION_CONCEPT_KEYS[seccion] || Object.keys(conceptos);
      keys.forEach(key => {
        const fullKey = `${seccion}.${key}`;
        const current = conceptos[key] || { estado: '', observaciones: '' };
        if (!YES_NO_CONCEPT_KEYS.has(fullKey) && current.estado === '') {
          conceptos[key] = { ...current, estado: 'bueno' };
          newlyMarked.add(fullKey);
        }
      });
      return { ...prev, [seccion]: { ...prev[seccion], conceptos } };
    });
    setAutoMarkedConcepts(newlyMarked);
    setMarkAllDialogSection(null);
    setSnackbar({
      open: true,
      message: 'Se marcaron como Bueno los conceptos sin evaluar de esta sección. Quedan resaltados en ámbar hasta que los confirmes uno por uno.',
      severity: 'info'
    });
  };

  // Botón "Marcar todos como Bueno" de una sección -- se coloca junto al título de cada
  // tabla de conceptos, justo arriba de lo que va a modificar.
  const renderMarkSectionButton = (seccion) => {
    const pending = countPendingInSection(seccion);
    return (
      <Tooltip title={
        pending === 0
          ? 'No hay conceptos sin evaluar en esta sección'
          : `Marca los ${pending} conceptos sin evaluar de esta sección como Bueno (no toca los que ya capturaste)`
      }>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DoneAllIcon fontSize="small" />}
            onClick={() => setMarkAllDialogSection(seccion)}
            disabled={pending === 0}
            sx={{
              textTransform: 'none', fontWeight: 600, color: '#e65100', borderColor: '#ffb74d',
              '&:hover': { borderColor: '#e65100', bgcolor: '#fff3e0' }
            }}
          >
            Marcar todos como Bueno
          </Button>
        </span>
      </Tooltip>
    );
  };

  const handleObservacionesChange = (seccion, concepto, valor) => {
    setEvaluaciones(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        conceptos: {
          ...prev[seccion].conceptos,
          [concepto]: { ...prev[seccion].conceptos[concepto], observaciones: valor }
        }
      }
    }));
  };

  const handleCampoTextoChange = (seccion, campo, valor) => {
    setEvaluaciones(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        camposTexto: { ...prev[seccion].camposTexto, [campo]: valor }
      }
    }));
  };

  // Total de conceptos por sección (fijo)
  const TOTAL_CONCEPTOS_POR_SECCION = {
    estructural: 16,
    equipoEspecifico: 5,
    instalacionElectrica: 9,
    instalacionGas: 4,
    instalacionHidrosanitaria: 7,
    sistemasVentilacion: 3,
    poblacion: 4,
    recipientesPresion: 8,
    tanquesAlmacenamiento: 8
  };

  const calcularProgreso = (seccion) => {
    const conceptosObj = evaluaciones[seccion]?.conceptos || {};
    const keys = Object.keys(conceptosObj).filter(k => k !== OTRO_EQUIPO_KEY);
    // Un concepto auto-marcado ya tiene un valor capturado ("Bueno"), así que sí cuenta para
    // el avance -- la señal de "todavía sin revisar" queda en el resaltado ámbar y el chip
    // "Auto · confirmar" de cada fila, no en detener la barra de progreso.
    const completados = keys.filter(k => conceptosObj[k].estado !== '').length;
    const total = TOTAL_CONCEPTOS_POR_SECCION[seccion] || keys.length;
    return { completados, total, porcentaje: total > 0 ? (completados / total) * 100 : 0 };
  };

  const renderConceptoRow = (seccion, conceptoKey, label) => {
    const concepto = evaluaciones[seccion]?.conceptos?.[conceptoKey] || { estado: '', observaciones: '' };
    const fullKey = `${seccion}.${conceptoKey}`;
    const isAutoMarked = autoMarkedConcepts.has(fullKey);

    return (
      <TableRow
        key={conceptoKey}
        sx={{
          '&:hover': { backgroundColor: isAutoMarked ? '#ffecb3' : '#f6f8fa' },
          '& td': { borderBottom: '1px solid #e1e4e8' },
          ...(isAutoMarked ? { backgroundColor: '#fff8e1' } : {})
        }}
      >
        <TableCell sx={{ width: '5%', textAlign: 'center' }}>
          {isAutoMarked ? (
            <Tooltip title="Marcado automáticamente como Bueno -- todavía sin revisar">
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 20 }} />
            </Tooltip>
          ) : concepto.estado ? (
            <CheckCircleIcon sx={{ color: '#28a745', fontSize: 20 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#d1d5da', fontSize: 20 }} />
          )}
        </TableCell>
        <TableCell sx={{ width: '25%', fontWeight: 600, color: '#24292e' }}>
          {label}
          {isAutoMarked && (
            <Tooltip title="Se marcó automáticamente como Bueno. Toca aquí para confirmar que ya lo revisaste.">
              <Chip
                label="Auto · confirmar"
                size="small"
                onClick={() => confirmAutoMarked(seccion, conceptoKey)}
                sx={{
                  display: 'block', width: 'fit-content', mt: 0.5, height: 20, fontSize: '0.65rem', fontWeight: 700,
                  bgcolor: '#ffe0b2', color: '#e65100', cursor: 'pointer', '&:hover': { bgcolor: '#ffcc80' }
                }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ width: '30%' }}>
          <ToggleButtonGroup
            value={concepto.estado}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) handleEstadoChange(seccion, conceptoKey, newValue);
            }}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.5,
                '&.Mui-selected': { color: 'white' }
              }
            }}
          >
            <ToggleButton value="bueno" sx={{ '&.Mui-selected': { backgroundColor: '#28a745 !important' } }}>
              ✓ Bueno
            </ToggleButton>
            <ToggleButton value="regular" sx={{ '&.Mui-selected': { backgroundColor: '#ffc107 !important' } }}>
              ~ Regular
            </ToggleButton>
            <ToggleButton value="malo" sx={{ '&.Mui-selected': { backgroundColor: '#dc3545 !important' } }}>
              ✗ Malo
            </ToggleButton>
            <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#6c757d !important' } }}>
              N/A
            </ToggleButton>
          </ToggleButtonGroup>
        </TableCell>
        <TableCell sx={{ width: '40%' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Observaciones..."
            value={concepto.observaciones}
            onChange={(e) => handleObservacionesChange(seccion, conceptoKey, e.target.value)}
            multiline
            rows={1}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const renderYesNoRow = (seccion, conceptoKey, label, domId) => {
    const concepto = evaluaciones[seccion]?.conceptos?.[conceptoKey] || { estado: '', observaciones: '' };

    return (
      <TableRow
        key={conceptoKey}
        id={domId}
        sx={{
          '&:hover': { backgroundColor: '#f6f8fa' },
          '& td': { borderBottom: '1px solid #e1e4e8' }
        }}
      >
        <TableCell sx={{ width: '5%', textAlign: 'center' }}>
          {concepto.estado ? (
            <CheckCircleIcon sx={{ color: '#28a745', fontSize: 20 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#d1d5da', fontSize: 20 }} />
          )}
        </TableCell>
        <TableCell sx={{ width: '25%', fontWeight: 600, color: '#24292e' }}>
          {label}
        </TableCell>
        <TableCell sx={{ width: '30%' }}>
          <ToggleButtonGroup
            value={concepto.estado}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) handleEstadoChange(seccion, conceptoKey, newValue);
            }}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.5,
                '&.Mui-selected': { color: 'white' }
              }
            }}
          >
            <ToggleButton value="si" sx={{ '&.Mui-selected': { backgroundColor: '#28a745 !important' } }}>
              ✓ Sí
            </ToggleButton>
            <ToggleButton value="no" sx={{ '&.Mui-selected': { backgroundColor: '#dc3545 !important' } }}>
              ✗ No
            </ToggleButton>
            <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#6c757d !important' } }}>
              N/A
            </ToggleButton>
          </ToggleButtonGroup>
        </TableCell>
        <TableCell sx={{ width: '40%' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Observaciones..."
            value={concepto.observaciones}
            onChange={(e) => handleObservacionesChange(seccion, conceptoKey, e.target.value)}
            multiline
            rows={1}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const progreso1 = calcularProgreso('estructural');
  const progreso2 = calcularProgreso('equipoEspecifico');
  const progreso3 = calcularProgreso('instalacionElectrica');
  const progreso4 = calcularProgreso('instalacionGas');
  const progreso5 = calcularProgreso('instalacionHidrosanitaria');
  const progreso6 = calcularProgreso('sistemasVentilacion');
  const progreso7 = calcularProgreso('poblacion');
  const progreso8 = calcularProgreso('recipientesPresion');
  const progreso9 = calcularProgreso('tanquesAlmacenamiento');

  // Calcular progreso global basado en todas las secciones
  const calcularProgresoGlobal = () => {
    const progresos = [progreso1, progreso2, progreso3, progreso4, progreso5, progreso6, progreso7, progreso8, progreso9];
    const totalCompletados = progresos.reduce((sum, p) => sum + p.completados, 0);
    const totalConceptos = progresos.reduce((sum, p) => sum + p.total, 0);
    const porcentaje = totalConceptos > 0 ? (totalCompletados / totalConceptos) * 100 : 0;
    return { completados: totalCompletados, total: totalConceptos, porcentaje };
  };

  const progresoGlobal = calcularProgresoGlobal();

  // Mostrar loading mientras carga
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Cargando evaluación...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'warning' || snackbar.severity === 'info' ? 10000 : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ maxWidth: 560 }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', fontSize: '0.95rem', alignItems: 'center' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Banner de estado offline -- ver offlineSyncEngine.js. Persistente (no es un Snackbar
          que desaparece solo) porque el inspector puede tardar en volver a tener señal. */}
      {offlineBanner === 'cold-start' && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, fontWeight: 500 }}>
          Sin conexión — estás capturando esta evaluación desde cero en este dispositivo. Esto reemplazará lo que exista en el servidor al sincronizar.
        </Alert>
      )}
      {offlineBanner === 'pending-sync' && (
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, fontWeight: 500 }}>
          Tienes cambios guardados en este dispositivo aún sin sincronizar. Se enviarán solos en cuanto recuperes señal.
        </Alert>
      )}
      {offlineBanner === 'sync-rejected' && syncRejectedCode === 'CONFLICT' && (
        <Alert severity="warning" sx={{ mb: 2, fontWeight: 500 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button color="inherit" size="small" variant="outlined" onClick={() => resolveRejectedDraft('keep-mine')}>Usar la mía (recomendado)</Button>
              <Button color="inherit" size="small" onClick={() => resolveRejectedDraft('use-server')}>Usar la del sistema</Button>
            </Box>
          )}>
          {syncRejectedMessage}{conflictDetailText}{draftInfoText} Tus cambios siguen en este dispositivo y no se han enviado. <strong>Usar la mía</strong> reemplaza la versión del sistema con lo capturado aquí; <strong>Usar la del sistema</strong> borra lo capturado en este dispositivo.
        </Alert>
      )}
      {offlineBanner === 'sync-rejected' && syncRejectedCode === 'EVALUATION_NOT_FOUND' && (
        <Alert severity="warning" sx={{ mb: 2, fontWeight: 500 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button color="inherit" size="small" variant="outlined" onClick={() => resolveRejectedDraft('save-as-new')}>Guardar como nueva</Button>
              <Button color="inherit" size="small" onClick={() => setDiscardDraftDialogOpen(true)}>Descartar borrador</Button>
            </Box>
          )}>
          {syncRejectedMessage}{draftInfoText} Lo capturado sigue en este dispositivo. Puedes subirlo como una evaluación nueva de este ente o descartarlo.
        </Alert>
      )}
      {offlineBanner === 'sync-rejected' && !['CONFLICT', 'EVALUATION_NOT_FOUND'].includes(syncRejectedCode) && (
        <Alert severity="error" sx={{ mb: 2, fontWeight: 500 }}>
          El servidor no aceptó los cambios guardados en este dispositivo: {syncRejectedMessage}
          {' '}Se dejaron de enviar; tus datos siguen aquí. Si te reasignan el ente, cualquier cambio que guardes los volverá a enviar.
        </Alert>
      )}
      <Dialog open={discardDraftDialogOpen} onClose={() => setDiscardDraftDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>¿Descartar el borrador?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borrará de este dispositivo todo lo capturado para este ente. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardDraftDialogOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => resolveRejectedDraft('discard')}>Descartar</Button>
        </DialogActions>
      </Dialog>

      <ReturnToValidationBanner info={returnToValidation}
        onReturn={async () => {
          // Si el guardado se detuvo (p. ej. sustancia sin cantidad), se queda para corregirlo
          if (await handleSave(false) === false) return;
          goBackToValidation(navigate, returnToValidation);
        }} />

      {isAsync && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: '#f9a825', bgcolor: '#fffde7', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CloudOffIcon sx={{ color: '#f57f17' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Inspección sin ente asignado</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se guarda solo en este dispositivo. Cuando tengas señal, asígnala a su ente desde Programas → "Inspecciones sin asignar".
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Nombre del sitio o negocio" value={asyncMeta.siteName || ''}
                onChange={(e) => updateAsyncMeta('siteName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Dirección / referencias" value={asyncMeta.notes || ''}
                onChange={(e) => updateAsyncMeta('notes', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" startIcon={locating ? <CircularProgress size={16} /> : <MyLocationIcon />}
                  onClick={captureGps} disabled={locating} sx={{ textTransform: 'none' }}>
                  {asyncMeta.gps ? 'Actualizar ubicación' : 'Tomar ubicación GPS'}
                </Button>
                <Typography variant="body2" color={asyncMeta.gps ? 'text.primary' : 'text.secondary'}>
                  {asyncMeta.gps
                    ? `${asyncMeta.gps.lat.toFixed(6)}, ${asyncMeta.gps.lng.toFixed(6)} (±${asyncMeta.gps.accuracy} m)`
                    : 'Sin ubicación: sirve para sugerir el ente más cercano al asignarla.'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      {offlineBanner === 'auth-required' && (
        <Alert severity="error" sx={{ mb: 2, fontWeight: 500 }}>
          Tu sesión venció mientras estabas sin conexión. Tus datos siguen guardados en este dispositivo -- vuelve a iniciar sesión para sincronizarlos.
        </Alert>
      )}

      {/* Header con información de la evaluación */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => (isAsync ? navigate('/servicios/programas') : navigate(-1))}
              sx={{ textTransform: 'none', fontWeight: 600, mb: 1, color: 'text.secondary' }}
            >
              {isAsync ? 'Regresar a Programas' : 'Regresar a la orden de trabajo'}
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
              📋 Evaluación de Inspección
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Complete todas las secciones para generar el dictamen final
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              {/* Indicador de ente actual - siempre visible */}
              <Chip 
                icon={selectedEntity.entityType === 'MATRIZ' ? <BusinessIcon /> : <StoreIcon />}
                label={isAsync ? 'Ente: sin asignar' : `Ente: ${selectedEntity.entityName || (selectedEntity.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal')}`}
                size="small" 
                color="info"
                sx={{ fontWeight: 700 }}
              />
              {evaluationInfo && (
                <>
                  {evaluationInfo.quotationFolio && (
                    <Chip label={`PIPC: ${evaluationInfo.quotationFolio}`} size="small" color="primary" variant="outlined" />
                  )}
                  {(evaluationInfo.companyName || navCompanyName) && (
                    <Chip label={`Empresa: ${evaluationInfo.companyName || navCompanyName}`} size="small" />
                  )}
                  {evaluationInfo.branchName && (
                    <Chip label={`Sucursal: ${evaluationInfo.branchName}`} size="small" color="secondary" variant="outlined" />
                  )}
                  {evaluationInfo.clientName && (
                    <Chip label={`Cliente: ${evaluationInfo.clientName}`} size="small" />
                  )}
                  <Chip 
                    label={`Estado: ${evaluationInfo.status}`} 
                    size="small" 
                    color={evaluationInfo.status === 'COMPLETED' ? 'success' : 'warning'}
                  />
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={() => handleSave(false)}
                disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
              {!isAsync && (<>
              <Tooltip title="Copiar esta evaluación a otras sucursales">
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => setCloneDialogOpen(true)}
                    disabled={!evaluationId || !entitiesStatus?.branches?.length}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Clonar
                  </Button>
                </span>
              </Tooltip>
              <Button 
                variant="contained" 
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleComplete}
                disabled={saving || !evaluationId || evaluationInfo?.status === 'COMPLETED'}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
              >
                Completar Evaluación
              </Button>
              </>)}
            </Box>
            {lastSaved && (
              <Typography variant="caption" color="text.secondary">
                Último guardado: {lastSaved.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </Typography>
            )}
            {autoSaveEnabled && (evaluationId || isAsync) && (
              <Typography variant="caption" color="success.main">
                ✓ Autoguardado activado (cada 30s)
              </Typography>
            )}
          </Box>
        </Box>

        {/* Selector de Ente (Matriz / Sucursales) */}
        {entitiesStatus && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <SwapHorizIcon sx={{ color: '#1565c0' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565c0' }}>
                Seleccionar Ente a Evaluar
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {/* Boton Matriz -- solo si el usuario tiene este ente asignado (el backend
                  devuelve matriz=null para un Ejecutivo de Operaciones no asignado ahí) */}
              {entitiesStatus.matriz && (
                <Button
                  variant={selectedEntity.entityType === 'MATRIZ' ? 'contained' : 'outlined'}
                  startIcon={<BusinessIcon />}
                  onClick={() => handleEntityChange({ entityType: 'MATRIZ', branchId: null, entityName: entitiesStatus.matriz?.entityName || 'Matriz' })}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: 2,
                    ...(selectedEntity.entityType === 'MATRIZ' ? { bgcolor: '#1565c0' } : {})
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {entitiesStatus.matriz?.entityName || 'Matriz'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {entityStatusLabel(entitiesStatus.matriz)}
                    </Typography>
                  </Box>
                </Button>
              )}

              {/* Botones Sucursales */}
              {entitiesStatus.branches?.map((branch) => (
                <Button
                  key={branch.branchId}
                  variant={selectedEntity.branchId === branch.branchId ? 'contained' : 'outlined'}
                  startIcon={<StoreIcon />}
                  onClick={() => handleEntityChange({ entityType: 'SUCURSAL', branchId: branch.branchId, entityName: branch.entityName })}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: 2,
                    ...(selectedEntity.branchId === branch.branchId ? { bgcolor: '#1565c0' } : {})
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {branch.entityName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {entityStatusLabel(branch)}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </Paper>
        )}

        {/* Barra de progreso global */}
        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Progreso Global
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {progresoGlobal.completados} de {progresoGlobal.total} conceptos
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progresoGlobal.porcentaje}
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progresoGlobal.porcentaje === 100 ? '#28a745' : '#1976d2'
                  }
                }}
              />
            </Box>
            <Chip 
              label={`${progresoGlobal.porcentaje.toFixed(1)}%`}
              sx={{ 
                fontWeight: 700, 
                fontSize: '1rem',
                bgcolor: progresoGlobal.porcentaje === 100 ? '#28a745' : '#1976d2',
                color: 'white'
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Acordeón 1: Evaluación Estructural */}
      <Accordion 
        expanded={expanded === 'estructural'} 
        onChange={handleAccordionChange('estructural')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <FoundationIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                🏗️ Evaluación Estructural
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso1.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso1.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso1.completados}/${progreso1.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso1.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Conceptos a Evaluar
              </Typography>
              {renderMarkSectionButton('estructural')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 'ventanales', label: 'Ventanales' },
                    { key: 'anunciosVolados', label: 'Anuncios Volados' },
                    { key: 'anunciosPanorámicos', label: 'Anuncios Panorámicos' },
                    { key: 'marquesinas', label: 'Marquesinas' },
                    { key: 'acabadosFachada', label: 'Acabados de la Fachada' },
                    { key: 'pretilesBalcones', label: 'Pretiles de Balcones' },
                    { key: 'inclinacionInmueble', label: 'Inclinación del Inmueble' },
                    { key: 'cimentacion', label: 'Cimentación' },
                    { key: 'columnas', label: 'Columnas' },
                    { key: 'muros', label: 'Muros' },
                    { key: 'bardasMallas', label: 'Bardas, Mallas o Cercas' },
                    { key: 'escaleras', label: 'Escaleras' },
                    { key: 'arboles', label: 'Árboles' },
                    { key: 'rampasAuto', label: 'Rampas para Auto' },
                    { key: 'pasosDesnivel', label: 'Pasos a Desnivel para Vehículos' },
                    { key: 'estacionamiento', label: 'Estacionamiento' }
                  ]
                    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
                    .map(concepto => renderConceptoRow('estructural', concepto.key, concepto.label))
                  }
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Información del Dictamen Estructural
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen Estructural</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-ESTRUCTURAL_folioDictamen"
                  value={evaluaciones.estructural.camposTexto.folioDictamen}
                  onChange={(e) => handleCampoTextoChange('estructural', 'folioDictamen', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Vigencia / Fecha del Dictamen</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-ESTRUCTURAL_vigenciaFecha"
                  value={evaluaciones.estructural.camposTexto.vigenciaFecha}
                  onChange={(e) => handleCampoTextoChange('estructural', 'vigenciaFecha', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Elaborado por</Typography>
                <TextField fullWidth size="small" placeholder="Nombre completo..."
                  id="field-ESTRUCTURAL_elaboradoPor"
                  value={evaluaciones.estructural.camposTexto.elaboradoPor}
                  onChange={(e) => handleCampoTextoChange('estructural', 'elaboradoPor', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Registro D.R.O. / Cédula Profesional</Typography>
                <TextField fullWidth size="small" placeholder="No. de registro o cédula..."
                  id="field-ESTRUCTURAL_registroDRO"
                  value={evaluaciones.estructural.camposTexto.registroDRO}
                  onChange={(e) => handleCampoTextoChange('estructural', 'registroDRO', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={5} placeholder="Describa las observaciones y recomendaciones..."
                  id="field-ESTRUCTURAL_observacionesGenerales"
                  value={evaluaciones.estructural.camposTexto.observacionesGenerales}
                  onChange={(e) => handleCampoTextoChange('estructural', 'observacionesGenerales', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 2: Equipo Específico */}
      <Accordion 
        expanded={expanded === 'equipoEspecifico'} 
        onChange={handleAccordionChange('equipoEspecifico')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <BuildIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                ⚙️ Evaluación de Equipo Específico
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso2.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso2.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso2.completados}/${progreso2.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso2.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Equipos a Evaluar
              </Typography>
              {renderMarkSectionButton('equipoEspecifico')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 'bombas', label: 'Bombas' },
                    { key: 'moduloDespacho', label: 'Módulo de Despacho' },
                    { key: 'parosEmergencia', label: 'Paros de Emergencia' },
                    { key: 'mangueraDescarga', label: 'Manguera de Descarga' },
                    { key: 'montacargas', label: 'Montacargas' }
                  ]
                    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
                    .map(concepto => renderConceptoRow('equipoEspecifico', concepto.key, concepto.label))
                  }
                  {renderConceptoRow('equipoEspecifico', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Dictamen NOM-005-ASEA-2016
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen NOM-005-ASEA-2016</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-EQUIPO_ESPECIFICO_folioNOM005ASEA"
                  value={evaluaciones.equipoEspecifico.camposTexto.folioNOM005ASEA}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'folioNOM005ASEA', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha de Emisión</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-EQUIPO_ESPECIFICO_fechaEmisionASEA"
                  value={evaluaciones.equipoEspecifico.camposTexto.fechaEmisionASEA}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'fechaEmisionASEA', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="Nombre de la unidad..."
                  id="field-EQUIPO_ESPECIFICO_unidadVerificacionASEA"
                  value={evaluaciones.equipoEspecifico.camposTexto.unidadVerificacionASEA}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'unidadVerificacionASEA', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Registro de la Unidad</Typography>
                <TextField fullWidth size="small" placeholder="No. de registro..."
                  id="field-EQUIPO_ESPECIFICO_registroUnidadASEA"
                  value={evaluaciones.equipoEspecifico.camposTexto.registroUnidadASEA}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'registroUnidadASEA', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={4} placeholder="Describa las observaciones..."
                  id="field-EQUIPO_ESPECIFICO_observacionesASEA"
                  value={evaluaciones.equipoEspecifico.camposTexto.observacionesASEA}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'observacionesASEA', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Dictamen NOM-005-SESH-2010 (Montacargas con Gas)
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen NOM-005-SESH-2010</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-EQUIPO_ESPECIFICO_folioNOM005SESH"
                  value={evaluaciones.equipoEspecifico.camposTexto.folioNOM005SESH}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'folioNOM005SESH', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>En caso de contar con montacargas que utilicen gas</Typography>
                <TextField fullWidth size="small" placeholder="Especifique el tipo de gas y cantidad..."
                  id="field-EQUIPO_ESPECIFICO_casoMontacargas"
                  value={evaluaciones.equipoEspecifico.camposTexto.casoMontacargas}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'casoMontacargas', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha de Emisión</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-EQUIPO_ESPECIFICO_fechaEmisionSESH"
                  value={evaluaciones.equipoEspecifico.camposTexto.fechaEmisionSESH}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'fechaEmisionSESH', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="Nombre de la unidad..."
                  id="field-EQUIPO_ESPECIFICO_unidadVerificacionSESH"
                  value={evaluaciones.equipoEspecifico.camposTexto.unidadVerificacionSESH}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'unidadVerificacionSESH', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={4} placeholder="Describa las observaciones..."
                  id="field-EQUIPO_ESPECIFICO_observacionesSESH"
                  value={evaluaciones.equipoEspecifico.camposTexto.observacionesSESH}
                  onChange={(e) => handleCampoTextoChange('equipoEspecifico', 'observacionesSESH', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 3: Instalación Eléctrica */}
      <Accordion 
        expanded={expanded === 'instalacionElectrica'} 
        onChange={handleAccordionChange('instalacionElectrica')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <ElectricBoltIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                ⚡ Evaluación de la Instalación Eléctrica
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso3.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso3.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso3.completados}/${progreso3.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso3.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Instalaciones Eléctricas a Evaluar
              </Typography>
              {renderMarkSectionButton('instalacionElectrica')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 'transformadorElectrico', label: 'Transformador Eléctrico' },
                    { key: 'subestacionElectrica', label: 'Subestación Eléctrica' },
                    { key: 'postesDistribucion', label: 'Postes de Distribución' },
                    { key: 'tablerosElectricos', label: 'Tableros Eléctricos' },
                    { key: 'equiposComputo', label: 'Equipos de Cómputo' },
                    { key: 'socketsFocos', label: 'Sockets y Focos' },
                    { key: 'tomaCorrientes', label: 'Toma Corrientes' },
                    { key: 'plantaEmergencia', label: 'Planta de Emergencia' },
                    { key: 'aireAcondicionado', label: 'Aire Acondicionado' }
                  ]
                    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
                    .map(concepto => renderConceptoRow('instalacionElectrica', concepto.key, concepto.label))
                  }
                  {renderConceptoRow('instalacionElectrica', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Información del Dictamen Eléctrico NOM-001-SEDE-2012
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen Eléctrico NOM-001-SEDE-2012</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-INSTALACION_ELECTRICA_folioDictamenElectrico"
                  value={evaluaciones.instalacionElectrica.camposTexto.folioDictamenElectrico}
                  onChange={(e) => handleCampoTextoChange('instalacionElectrica', 'folioDictamenElectrico', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha del Dictamen Eléctrico</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-INSTALACION_ELECTRICA_fechaDictamen"
                  value={evaluaciones.instalacionElectrica.camposTexto.fechaDictamen}
                  onChange={(e) => handleCampoTextoChange('instalacionElectrica', 'fechaDictamen', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="Nombre de la unidad..."
                  id="field-INSTALACION_ELECTRICA_unidadVerificacion"
                  value={evaluaciones.instalacionElectrica.camposTexto.unidadVerificacion}
                  onChange={(e) => handleCampoTextoChange('instalacionElectrica', 'unidadVerificacion', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Registro de la Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="No. de registro..."
                  id="field-INSTALACION_ELECTRICA_registroUnidad"
                  value={evaluaciones.instalacionElectrica.camposTexto.registroUnidad}
                  onChange={(e) => handleCampoTextoChange('instalacionElectrica', 'registroUnidad', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={5} placeholder="Describa las observaciones y recomendaciones..."
                  id="field-INSTALACION_ELECTRICA_observacionesGenerales"
                  value={evaluaciones.instalacionElectrica.camposTexto.observacionesGenerales}
                  onChange={(e) => handleCampoTextoChange('instalacionElectrica', 'observacionesGenerales', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 4: Instalación de Gas */}
      <Accordion 
        expanded={expanded === 'instalacionGas'} 
        onChange={handleAccordionChange('instalacionGas')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <GasIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                🔥 Evaluación de la Instalación de Gas
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso4.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso4.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso4.completados}/${progreso4.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso4.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Instalaciones de Gas a Evaluar
              </Typography>
              {renderMarkSectionButton('instalacionGas')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 'tuberiaGas', label: 'Tubería de Gas' },
                    { key: 'tanqueMovil', label: 'Tanque Móvil' },
                    { key: 'tanqueEstacionario', label: 'Tanque Estacionario' },
                    { key: 'valvulasTanque', label: 'Válvulas del Tanque' }
                  ]
                    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
                    .map(concepto => renderConceptoRow('instalacionGas', concepto.key, concepto.label))
                  }
                  {renderConceptoRow('instalacionGas', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Información del Dictamen de Gas
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen de Gas</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-INSTALACION_GAS_folioDictamenGas"
                  value={evaluaciones.instalacionGas.camposTexto.folioDictamenGas}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'folioDictamenGas', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="Nombre de la unidad..."
                  id="field-INSTALACION_GAS_unidadVerificacion"
                  value={evaluaciones.instalacionGas.camposTexto.unidadVerificacion}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'unidadVerificacion', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Registro de la Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="No. de registro..."
                  id="field-INSTALACION_GAS_registroUnidad"
                  value={evaluaciones.instalacionGas.camposTexto.registroUnidad}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'registroUnidad', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha del Dictamen</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-INSTALACION_GAS_fechaDictamen"
                  value={evaluaciones.instalacionGas.camposTexto.fechaDictamen}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'fechaDictamen', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha de Fabricación de los Tanques</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-INSTALACION_GAS_fechaFabricacionTanques"
                  value={evaluaciones.instalacionGas.camposTexto.fechaFabricacionTanques}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'fechaFabricacionTanques', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio Dictamen NOM-013-SEDG-2022</Typography>
                <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>En caso de contar con más de 10 años de fabricación</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-INSTALACION_GAS_folioNOM013"
                  value={evaluaciones.instalacionGas.camposTexto.folioNOM013}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'folioNOM013', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha del Dictamen NOM-013</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-INSTALACION_GAS_fechaDictamenNOM013"
                  value={evaluaciones.instalacionGas.camposTexto.fechaDictamenNOM013}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'fechaDictamenNOM013', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={5} placeholder="Describa las observaciones y recomendaciones..."
                  id="field-INSTALACION_GAS_observacionesGenerales"
                  value={evaluaciones.instalacionGas.camposTexto.observacionesGenerales}
                  onChange={(e) => handleCampoTextoChange('instalacionGas', 'observacionesGenerales', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 5: Instalación Hidrosanitaria */}
      <Accordion 
        expanded={expanded === 'instalacionHidrosanitaria'} 
        onChange={handleAccordionChange('instalacionHidrosanitaria')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <WaterIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                💧 Evaluación de la Instalación Hidrosanitaria
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso5.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso5.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso5.completados}/${progreso5.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso5.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Instalaciones Hidrosanitarias a Evaluar
              </Typography>
              {renderMarkSectionButton('instalacionHidrosanitaria')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderConceptoRow('instalacionHidrosanitaria', 'cisterna', 'Cisterna')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'tuberias', 'Tuberías')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'drenaje', 'Drenaje')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'mueblesSanitarios', 'Muebles Sanitarios')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'bombaHidraulica', 'Bomba Hidráulica')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'tomaSiamesa', 'Toma Siamesa')}
                  {renderConceptoRow('instalacionHidrosanitaria', 'aljibeDeposito', 'Aljibe o Depósito de Agua')}
                  {renderConceptoRow('instalacionHidrosanitaria', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 6: Sistemas de Ventilación */}
      <Accordion 
        expanded={expanded === 'sistemasVentilacion'} 
        onChange={handleAccordionChange('sistemasVentilacion')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <AirIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                🌬️ Evaluación de los Sistemas de Ventilación
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso6.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso6.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso6.completados}/${progreso6.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso6.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Sistemas de Ventilación a Evaluar
              </Typography>
              {renderMarkSectionButton('sistemasVentilacion')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderConceptoRow('sistemasVentilacion', 'extractores', 'Extractores')}
                  {renderConceptoRow('sistemasVentilacion', 'ventiladores', 'Ventiladores')}
                  {renderConceptoRow('sistemasVentilacion', 'aireAcondicionado', 'Aire Acondicionado')}
                  {renderConceptoRow('sistemasVentilacion', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 7: Población */}
      <Accordion 
        expanded={expanded === 'poblacion'} 
        onChange={handleAccordionChange('poblacion')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <PeopleIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                👥 Evaluación de la Población
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso7.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso7.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso7.completados}/${progreso7.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso7.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Aspectos de la Población a Evaluar
              </Typography>
              {renderMarkSectionButton('poblacion')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderConceptoRow('poblacion', 'estadoSalud', 'Estado de Salud')}
                  {renderConceptoRow('poblacion', 'estadoPsicologico', 'Estado Psicológico')}
                  {renderYesNoRow('poblacion', 'trabajadoresDiscapacidad', '¿El centro de trabajo cuenta con trabajadores con discapacidad?', 'field-TRABAJADORES_DISCAPACIDAD')}
                  {renderConceptoRow('poblacion', 'adaptacionDiscapacidad', 'El centro de trabajo se encuentra adaptado para las necesidades de personas con discapacidad')}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 8: Recipientes Sujetos a Presión */}
      <Accordion 
        expanded={expanded === 'recipientesPresion'} 
        onChange={handleAccordionChange('recipientesPresion')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <CompressIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                ⚙️ Recipientes Sujetos a Presión
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso8.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso8.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso8.completados}/${progreso8.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso8.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Recipientes a Evaluar
              </Typography>
              {renderMarkSectionButton('recipientesPresion')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderConceptoRow('recipientesPresion', 'tanquesHidroneumaticos', 'Tanques Hidroneumáticos')}
                  {renderConceptoRow('recipientesPresion', 'calderas', 'Calderas')}
                  {renderConceptoRow('recipientesPresion', 'autotanques', 'Autotanques')}
                  {renderConceptoRow('recipientesPresion', 'evaporadoras', 'Evaporadoras')}
                  {renderConceptoRow('recipientesPresion', 'intercambiadorCalor', 'Intercambiador de Calor')}
                  {renderConceptoRow('recipientesPresion', 'chillerCondensador', 'Chiller/Condensador')}
                  {renderConceptoRow('recipientesPresion', 'compresor', 'Compresor')}
                  {renderConceptoRow('recipientesPresion', 'otro', 'Otro (especificar en observaciones)')}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
              Dictamen NOM-020-STPS-2011
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Folio del Dictamen NOM-020-STPS-2011</Typography>
                <TextField fullWidth size="small" placeholder="Ingrese el folio..."
                  id="field-RECIPIENTES_PRESION_folioNOM020"
                  value={evaluaciones.recipientesPresion.camposTexto.folioNOM020}
                  onChange={(e) => handleCampoTextoChange('recipientesPresion', 'folioNOM020', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha de Emisión</Typography>
                <TextField fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  id="field-RECIPIENTES_PRESION_fechaEmision"
                  value={evaluaciones.recipientesPresion.camposTexto.fechaEmision}
                  onChange={(e) => handleCampoTextoChange('recipientesPresion', 'fechaEmision', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="Nombre de la unidad..."
                  id="field-RECIPIENTES_PRESION_unidadVerificacion"
                  value={evaluaciones.recipientesPresion.camposTexto.unidadVerificacion}
                  onChange={(e) => handleCampoTextoChange('recipientesPresion', 'unidadVerificacion', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Registro de la Unidad de Verificación</Typography>
                <TextField fullWidth size="small" placeholder="No. de registro..."
                  id="field-RECIPIENTES_PRESION_registroUnidad"
                  value={evaluaciones.recipientesPresion.camposTexto.registroUnidad}
                  onChange={(e) => handleCampoTextoChange('recipientesPresion', 'registroUnidad', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Observaciones y Recomendaciones</Typography>
                <TextField fullWidth size="small" multiline rows={5} placeholder="Describa las observaciones y recomendaciones..."
                  id="field-RECIPIENTES_PRESION_observacionesGenerales"
                  value={evaluaciones.recipientesPresion.camposTexto.observacionesGenerales}
                  onChange={(e) => handleCampoTextoChange('recipientesPresion', 'observacionesGenerales', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 9: Tanques de Almacenamiento */}
      <Accordion 
        expanded={expanded === 'tanquesAlmacenamiento'} 
        onChange={handleAccordionChange('tanquesAlmacenamiento')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <TankIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                🛢️ Tanques de Almacenamiento de Sustancias Químicas
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <LinearProgress 
                  variant="determinate" value={progreso9.porcentaje} 
                  sx={{ 
                    flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e1e4e8',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progreso9.porcentaje === 100 ? '#28a745' : '#1976d2'
                    }
                  }} 
                />
                <Chip 
                  label={`${progreso9.completados}/${progreso9.total}`} size="small"
                  sx={{ 
                    fontWeight: 700,
                    backgroundColor: progreso9.porcentaje === 100 ? '#28a745' : '#ffc107',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Sustancias Químicas Almacenadas
              </Typography>
              {renderMarkSectionButton('tanquesAlmacenamiento')}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '5%', textAlign: 'center' }}>✓</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Sustancia</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '30%' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderConceptoRow('tanquesAlmacenamiento', 'gasolina', 'Gasolina')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'diesel', 'Diesel')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'gasLP', 'Gas L.P.')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'solventes', 'Solventes')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'pinturas', 'Pinturas')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'amoniaco', 'Amoniaco')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'gasNatural', 'Gas Natural')}
                  {renderConceptoRow('tanquesAlmacenamiento', 'aceiteVehicular', 'Aceite Vehicular')}
                  {renderConceptoRow('tanquesAlmacenamiento', OTRO_EQUIPO_KEY, OTRO_EQUIPO_LABEL)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Acordeón 10: Sustancias Peligrosas, Gas y Protección Civil (datos "de sitio") */}
      <Accordion
        expanded={expanded === 'sustanciasGasPC'}
        onChange={handleAccordionChange('sustanciasGasPC')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#f6f8fa', borderRadius: 2, '&:hover': { backgroundColor: '#e1e4e8' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <ScienceIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#24292e' }}>
                🧪 Sustancias Peligrosas, Gas y Protección Civil
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Datos encontrados en sitio — heredados de la cotización, confírmalos o corrígelos
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
          <Box>
            <Box id="field-SUSTANCIAS_PELIGROSAS" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                Sustancias Peligrosas Presentes
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddHazardousSubstanceRow}
                disabled={!!siteData.noHazardousSubstances}>
                Agregar sustancia
              </Button>
            </Box>

            {/* "No aplica": el ente no maneja sustancias peligrosas -- el PIPC lo reporta así */}
            <FormControlLabel
              sx={{ mb: 1.5 }}
              control={
                <Checkbox
                  checked={!!siteData.noHazardousSubstances}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked && siteData.hazardousSubstances.length > 0
                      && !window.confirm('Se quitarán las sustancias capturadas para este ente. ¿Continuar?')) {
                      return;
                    }
                    setSiteData(prev => ({
                      ...prev,
                      noHazardousSubstances: checked,
                      hazardousSubstances: checked ? [] : prev.hazardousSubstances
                    }));
                  }}
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>No aplica: el ente no maneja sustancias peligrosas</Typography>}
            />
            {siteData.noHazardousSubstances && (
              <Alert severity="info" sx={{ mb: 3 }}>
                En el PIPC, Sustancias Peligrosas y sus tablas dirán "No aplica" y la capacidad de gas quedará en 0.
              </Alert>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e1e4e8', mb: 3, display: siteData.noHazardousSubstances ? 'none' : undefined }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f8fa' }}>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Sustancia</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Cantidad</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '25%' }}>Unidad</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: '10%' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {siteData.hazardousSubstances.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          Sin sustancias capturadas. Usa "Agregar sustancia" para registrar una.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {siteData.hazardousSubstances.map((item, index) => {
                    const quoted = findQuotedSubstance(item.substanceName);
                    const differsFromQuoted = quoted && (
                      Number(quoted.quantity) !== Number(item.quantity) || quoted.unit !== item.unit
                    );
                    return (
                      <TableRow key={item.id ?? `nueva-${index}`}>
                        <TableCell>
                          <TextField
                            select fullWidth size="small"
                            value={item.substanceName || ''}
                            onChange={(e) => handleHazardousSubstanceFieldChange(index, 'substanceName', e.target.value)}
                          >
                            <MenuItem value=""><em>Selecciona…</em></MenuItem>
                            {hazardousSubstancesCatalog.map((sub) => (
                              <MenuItem key={sub.id} value={sub.name}>{sub.name}</MenuItem>
                            ))}
                            {item.substanceName && !hazardousSubstancesCatalog.some(s => s.name === item.substanceName) && (
                              <MenuItem value={item.substanceName}>{item.substanceName}</MenuItem>
                            )}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" size="small" fullWidth
                            value={item.quantity ?? ''}
                            onChange={(e) => handleHazardousSubstanceFieldChange(index, 'quantity', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select size="small" fullWidth
                            value={item.unit || ''}
                            onChange={(e) => handleHazardousSubstanceFieldChange(index, 'unit', e.target.value)}
                          >
                            <MenuItem value=""><em>Selecciona…</em></MenuItem>
                            <MenuItem value="Litros">Litros</MenuItem>
                            <MenuItem value="Kilogramos">Kilogramos</MenuItem>
                            <MenuItem value="Toneladas">Toneladas</MenuItem>
                            <MenuItem value="Galones">Galones</MenuItem>
                            <MenuItem value="Metros cúbicos">Metros cúbicos</MenuItem>
                            {item.unit && !['Litros', 'Kilogramos', 'Toneladas', 'Galones', 'Metros cúbicos'].includes(item.unit) && (
                              <MenuItem value={item.unit}>{item.unit}</MenuItem>
                            )}
                          </TextField>
                          {quoted && (
                            <Typography variant="caption" color={differsFromQuoted ? 'warning.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                              Cotizado: {quoted.quantity} {quoted.unit}
                            </Typography>
                          )}
                          {/* "Gas LP" alimenta {CAPACIDAD_GAS} en el PIPC, que siempre reporta
                              litros -- si capturan la cantidad en otra unidad aquí, el documento
                              igual la reporta como litros, así que se los advertimos. */}
                          {item.substanceName === 'Gas LP' && item.unit && item.unit !== 'Litros' && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                              ⚠ Gas LP se reporta siempre en litros en el documento PIPC, sin importar la unidad elegida aquí.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => handleRemoveHazardousSubstanceRow(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1976d2' }}>
              Visto Bueno de Protección Civil
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Estatus</Typography>
                <TextField
                  id="field-vb"
                  select fullWidth size="small"
                  value={siteData.civilProtectionApproval}
                  onChange={(e) => handleSiteDataFieldChange('civilProtectionApproval', e.target.value)}
                >
                  <MenuItem value=""><em>Sin capturar</em></MenuItem>
                  <MenuItem value="si">Sí</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="no_aplica">No aplica</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Fecha de Liberación</Typography>
                <TextField
                  fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  disabled={siteData.civilProtectionApproval !== 'si'}
                  value={siteData.civilProtectionApprovalDate || ''}
                  onChange={(e) => handleSiteDataFieldChange('civilProtectionApprovalDate', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', mb: 0.5, display: 'block' }}>Vigencia</Typography>
                <TextField
                  fullWidth size="small" type="date"
                  InputLabelProps={{ shrink: true }}
                  disabled={siteData.civilProtectionApproval !== 'si'}
                  value={siteData.civilProtectionApprovalValidity || ''}
                  onChange={(e) => handleSiteDataFieldChange('civilProtectionApprovalValidity', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Dialog para Clonar Evaluación */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => { setCloneDialogOpen(false); setCloneTargets([]); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopyIcon color="primary" />
          Clonar Evaluación a Otros Entes
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccione las sucursales a las que desea copiar la evaluación actual.
            Se copiarán todos los conceptos y campos de texto.
          </Typography>
          {entitiesStatus?.branches?.length > 0 ? (
            <List>
              {entitiesStatus.branches
                .filter(b => b.branchId !== selectedEntity.branchId)
                .map((branch) => (
                  <ListItem key={branch.branchId} disablePadding>
                    <ListItemButton onClick={() => handleToggleCloneTarget(branch.branchId)}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={cloneTargets.includes(branch.branchId)}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={branch.entityName}
                        secondary={branch.hasEvaluation
                          ? `Ya tiene evaluación (${branch.progressPercentage?.toFixed(0)}%) — se sobrescribirá`
                          : 'Sin evaluación — se creará nueva'}
                      />
                      {branch.hasEvaluation && (
                        <Chip label="Existente" size="small" color="warning" variant="outlined" />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No hay sucursales disponibles para clonar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setCloneDialogOpen(false); setCloneTargets([]); }}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={cloning ? <CircularProgress size={20} /> : <ContentCopyIcon />}
            onClick={handleClone}
            disabled={cloning || cloneTargets.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {cloning ? 'Clonando...' : `Clonar a ${cloneTargets.length} ente(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para "Marcar todos como Bueno" -- reutilizado por las 9
          secciones; markAllDialogSection indica cuál lo abrió */}
      <Dialog
        open={Boolean(markAllDialogSection)}
        onClose={() => setMarkAllDialogSection(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#ff9800' }} />
          Marcar todos como Bueno
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Esto marcará como <strong>Bueno</strong> los <strong>{countPendingInSection(markAllDialogSection)}</strong> conceptos
            de esta sección que aún no has evaluado. No se toca ningún concepto que ya hayas capturado.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Los conceptos marcados así quedarán resaltados en ámbar como "pendientes de confirmar" —
            deberás revisarlos y confirmarlos uno por uno antes de completar la evaluación.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMarkAllDialogSection(null)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<DoneAllIcon />}
            onClick={() => handleMarkSectionGood(markAllDialogSection)}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
          >
            Marcar {countPendingInSection(markAllDialogSection)} conceptos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bloqueo/recordatorio al intentar "Completar Evaluación" -- mismo tratamiento (Dialog,
          no Snackbar) que "Marcar todos como Bueno" arriba, para que sea imposible de pasar por
          alto sin leerlo. */}
      <Dialog
        open={Boolean(completeIssueDialog)}
        onClose={() => setCompleteIssueDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          {completeIssueDialog?.severity === 'warning'
            ? <WarningAmberIcon sx={{ color: '#ff9800' }} />
            : <InfoOutlinedIcon sx={{ color: '#1976d2' }} />}
          {completeIssueDialog?.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div">
            {completeIssueDialog?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCompleteIssueDialog(null)}
            sx={{
              textTransform: 'none', fontWeight: 600,
              bgcolor: completeIssueDialog?.severity === 'warning' ? '#ff9800' : '#1976d2',
              '&:hover': { bgcolor: completeIssueDialog?.severity === 'warning' ? '#f57c00' : '#1565c0' }
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default EvaluacionInspeccionCompleta;
